<template>
  <modal
    v-if="!loading || fullGame.live"
    class="style-modal"
    @close-modal="closeModal"
  >
    <div>
      <div v-if="!offerMode">
        <!-- this is top header part of modal !-->
        <h5 class="my-0 mb-2 header-info text-center">
          {{ dayjs(fullGame.start).format('DD MMM hh:mm A') }}
          - {{ fullGame.participants[0].longName }} at
          {{ fullGame.participants[1].longName }}
          <span v-if="periodLabel" class="period-pill">{{ periodLabel }}</span>
          <span v-if="hasMustStart" class="must-start-pill">MUST START</span>
          <span v-if="isSeries" class="must-start-pill">SERIES</span>
          <WorldCupInjBadge v-if="isWorldCup" class="ml-1 d-inline-block" />
          <ToAdvanceBadge v-if="isToAdvance" class="ml-1 d-inline-block" />
          <h5
            v-if="fullGame.participants[0].mainPitcher && !isToAdvance"
            style="display: inline"
          >
            ({{ fullGame.participants[0].mainPitcher }})
          </h5>
        </h5>
        <h1 class="my-0 media header-info text-center">
          <div
            v-if="fullGame.league && fullGame.league.includes('PROPS')"
            class="propStyle"
          >
            {{ formatEventNameForDisplay(fullGame.eventName) }}
          </div>
          {{ formattedSideString }}
          <span id="main_odds">
            {{
              formatOdds(
                exceedsBalanceButCanBetMax ? maxOddsForDisplay : computedOdds,
                fullGame.league.toLowerCase(),
                betType,
                true,
              )
            }}
          </span>
          <font-awesome-icon
            v-if="showArrow"
            :icon="faArrowUp"
            :class="classArrow"
            size="xs"
          />
          <informational-tooltip
            v-if="userStore.user.viewOddsWithCommission"
            class="text-lowercase font-weight-bold"
            :text-content="`All displayed odds are adjusted to factor in commission`"
          />
        </h1>
        <div class="flex-nowrap align-items-center mt-3">
          <bid-ask-ladder
            :key="'bidaskladder'"
            :full-game="fullGame"
            :side="side"
            :offer-mode="false"
            :your-side-name="getSideString(fullGame, session, false)"
            :other-side-name="getOtherSideString(fullGame, session, false)"
            :take-bet-mode="true"
            :left-elements="
              betType === 'spread' ? alternativeSpreads.away : alternativeTotals
            "
            :right-elements="
              betType === 'spread' ? alternativeSpreads.home : alternativeTotals
            "
            :risk="effectiveRiskForLadder"
            :selected-bid-ids="selectedBidIds"
            :bid-ladder="bidLadder"
            :ask-ladder="askLadder"
            :spread-number="spread"
            :total-number="total"
            @select-bids="handleSelectBids"
            @open-dropdown="openDropdown"
            @add-width-to-bid-ladder="addWidthLadderArray"
          />
        </div>

        <!-- Live slippage dial — per-bet, always starts off (0%). Any value
             above 0 turns it on; no toggle. Colour warms grey → blue-purple
             as the tolerance is dialed up. -->
        <div
          v-if="showSlippageCard"
          class="arb-card mt-3"
          :style="{ '--slip-accent': slipAccentColor, '--slip-tint': slipTintColor }"
        >
          <!-- Static copy: nothing in this row changes while scrolling, so the
               only thing that moves is the bar + the % readout below. The
               icon tint is the one exception — it warms with the value. -->
          <div class="arb-card-message">
            <font-awesome-icon
              :icon="betSlippagePct > 0 ? faBolt : faExclamationTriangle"
              class="arb-card-status-icon"
            />
            <span class="arb-card-text">
              Live odds move fast &mdash; slippage lets a small move fill
              instead of canceling your bet.
              <a
                class="arb-card-learn"
                href="#"
                @click.stop.prevent="openSlippageInfo"
                >Learn more</a
              >
            </span>
          </div>
          <div class="arb-card-slider-row">
            <input
              type="range"
              class="slippage-slider"
              min="0"
              :max="liveSlippageMaxPct"
              step="any"
              :value="slipThumbPct"
              :style="{ '--fill': slipSliderFillStop }"
              @input="onDialInput($event)"
              @pointerdown="onSliderPointerDown"
              @pointermove="onSliderPointerMove"
              @pointerup="onSliderPointerUp"
              @pointercancel="onSliderPointerUp"
            />
            <span class="slippage-value">{{ betSlippagePct }}%</span>
          </div>
          <!-- Bottom action row: "set as default" button on the left, live
               worst-fill readout (updates as you scroll) on the right. -->
          <div class="arb-card-default-row">
            <button
              type="button"
              class="arb-card-default-btn"
              @click.stop="setSlippageDefault"
            >
              Set {{ betSlippagePct }}% as default
            </button>
            <span class="arb-card-floor-value">
              <template v-if="betSlippagePct > 0 && slipWorstDisplay">
                <span class="arb-card-floor-from">{{ slipPriceDisplay }}</span>
                <span class="arb-card-floor-arrow">&rarr;</span>
                <span class="arb-card-floor-to">{{ slipWorstDisplay }}</span>
              </template>
              <template v-else>&mdash;</template>
            </span>
          </div>
        </div>

        <div class="risk-win-items flex-nowrap row align-items-center mt-3">
          <div class="col-sm-6 col-12 align-items-center pr-2">
            <div
              class="input-group input-group-win-risk d-flex align-items-center"
            >
              <input
                ref="referencedRisk"
                v-two-decimal
                inputmode="decimal"
                maxlength="8"
                :value="risk"
                @input="handleRiskUpdate($event.target.value)"
                @blur="onFocusValues.risk = false"
              />
              <div class="input-group-button pr-2">
                <span class="available" @click="handleRiskQuick()">
                  {{ riskQuickLabel }}
                </span>
              </div>
              <div
                ref="riskDropdownRef"
                class="input-group-div"
                @click="
                  onFocusValues.win = false;
                  onFocusValues.risk = true;
                  dropdownRisk = !dropdownRisk;
                "
              >
                Risk
                <font-awesome-icon
                  :icon="dropdownRisk ? faAngleUp : faAngleDown"
                  role="button"
                  class="ml-2"
                />
                <div v-if="dropdownRisk" class="dropdown-menu" @click.stop>
                  <div
                    v-for="item in riskWinOptions"
                    :key="item.concat('RISK')"
                    @click="handleRiskDropdown(item)"
                  >
                    {{ item }}
                  </div>
                  <div class="quick-max" @click="handleRiskDropdown('max')">
                    Max
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-sm-6 col-12 align-items-center pl-2">
            <div
              class="input-group input-group-win-risk d-flex align-items-center"
            >
              <input
                v-two-decimal
                inputmode="decimal"
                maxlength="8"
                :readonly="session.sumUntaken < 10"
                :value="win"
                @input="handleWinUpdate($event.target.value)"
                @blur="onFocusValues.win = false"
              />
              <div class="input-group-button pr-2">
                <span class="available" @click="handleWinQuick()">
                  {{ winQuickLabel }}
                </span>
              </div>
              <div
                ref="winDropdownRef"
                class="input-group-div"
                @click="
                  onFocusValues.risk = false;
                  onFocusValues.win = true;
                  dropdownWin = !dropdownWin;
                "
              >
                To Win
                <font-awesome-icon
                  :icon="dropdownWin ? faAngleUp : faAngleDown"
                  role="button"
                  class="ml-2"
                />
                <div v-if="dropdownWin" class="dropdown-menu" @click.stop>
                  <div
                    v-for="item in riskWinOptions"
                    :key="item.concat('WIN')"
                    @click="handleWinDropdown(item)"
                  >
                    {{ item }}
                  </div>
                  <div class="quick-max" @click="handleWinDropdown('max')">
                    Max
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="canSeeRebates(userStore.user.username) && risk !== '' && parseFloat(risk) >= 1 && parseFloat(win) > 0" class="rebate-line mt-3">
          <span class="rebate-line__label">Offer Rebate (0.1%)</span>
          <span class="rebate-line__value">+${{ calculateRebateAmount(risk, win).toFixed(2) }}</span>
        </div>
        <alert-notification
          v-if="risk > userStore.user.maxLiability"
          type="primary"
          :class="{
            'mt-4': !showLimitFill,
          }"
        >
          <font-awesome-icon
            :icon="faCircleExclamation"
            class="mr-1 max-risk"
          />
          <span class="mr-1">Max Risk = ${{ userStore.user.maxLiability }}.</span>
          <span v-if="parseFloat(risk) <= 100000" class="font-regular">
            <a
              class="add-available-risk"
              @click="updateMaxRisk(parseFloat(risk))"
              >Update to ${{ risk }}</a
            >
          </span>
          <span v-else class="font-regular">
            Maximum allowed is $100,000
          </span>
        </alert-notification>

        <alert-notification
          v-if="available < getBetPlusCommission(risk)"
          type="error"
          class="mt-3"
        >
          <font-awesome-icon :icon="faExclamationTriangle" class="mr-1" />
          <span class="font-regular"
            >Risk + commission ({{
              roundToFixed2(
                getBetPlusCommission(
                  risk,
                  computedOdds,
                  props.fullGame.league.toLowerCase(),
                  props.session.type,
                ),
              )
            }}) > Available Balance (<a
              class="add-balance"
              @click="handleMaxButton()"
              >{{ Math.floor(available) }}</a
            >)</span
          >
        </alert-notification>
        <div
          v-if="loading && fullGame.live && !offerMode && !rebookPromptOpen"
          class="live-delay-banner"
        >
          <span class="live-delay-banner__dot" aria-hidden="true" />
          <span class="live-delay-banner__text">
            <strong>Live delay — holding {{ secondsByLeague() }}s.</strong>
            If odds get worse the bet cancels. If they get better you keep the
            better price.
          </span>
        </div>
        <div v-if="userStore.user.isLoggedIn" class="row mt-4">
          <div class="col-xs-12 mb-3 col-md-6">
            <slide-to-confirm
              v-if="isMobileViewport"
              :disabled="takeBetDisabled"
              :loading="loading"
              :variant="exceedsBalanceButCanBetMax ? 'max' : 'primary'"
              :aria-label="`Slide to take bet for $${exceedsBalanceButCanBetMax ? maxRiskForDisplay.toFixed(2) : risk === '' ? 0 : risk}`"
              @confirm="onSlideConfirm"
              @abandon="onSlideAbandon"
            >
              <b>
                Slide to take for ${{
                  exceedsBalanceButCanBetMax
                    ? maxRiskForDisplay.toFixed(2)
                    : risk === ''
                      ? 0
                      : risk
                }}
              </b>
              <span>
                to win ${{
                  exceedsBalanceButCanBetMax
                    ? maxWinForDisplay.toFixed(2)
                    : win === ''
                      ? 0
                      : win
                }}
              </span>
            </slide-to-confirm>
            <button
              v-else
              :disabled="takeBetDisabled"
              :class="['take-bet-btn', { 'btn-max-balance': exceedsBalanceButCanBetMax }]"
              @click="exceedsBalanceButCanBetMax ? takeBetAtMax() : takeBet()"
            >
              <b> Take bet for ${{ exceedsBalanceButCanBetMax ? maxRiskForDisplay.toFixed(2) : (risk === '' ? 0 : risk) }} </b> <br />
              to win ${{ exceedsBalanceButCanBetMax ? maxWinForDisplay.toFixed(2) : (win === '' ? 0 : win) }}
            </button>
          </div>
          <div
            :class="[
              !(
                (userStore.user.isLoggedIn &&
                  parseFloat(risk) <= parseFloat(maxTake)) ||
                risk === ''
              )
                ? 'col-xs-12 col-md-6'
                : 'col-xs-12 col-md-6',
            ]"
          >
            <button
              class="create-offer-btn"
              :disabled="loading"
              @click="setUpCreateOffer"
            >
              <b> Create New Offer </b>
              <br />
              {{ formattedSideString }}
            </button>
          </div>
        </div>
        <div v-else class="row mt-4 justify-content-center">
          <div class="col-xs-12 col-md-6">
            <button
              class="take-bet-btn"
              @click="checkAuthAndShowLogin"
            >
              <b> You must be logged in to bet </b>
              <br />
              Login or signup by clicking here
            </button>
          </div>
        </div>
        <select-spread-dropdown
          v-if="bidLadderSpread && ['total', 'spread'].includes(session.type)"
          v-show="session.type === 'spread' ? awaySpreadPopUp : totalPopUp"
          :game="fullGame"
          :bid-ladder-spread="bidLadderSpread"
          :left-elements="
            session.type === 'spread'
              ? alternativeSpreads.away
              : alternativeTotals
          "
          :right-elements="
            session.type === 'spread'
              ? alternativeSpreads.home
              : alternativeTotals
          "
          :popup="session.type === 'spread' ? 'awaySpreadPopUp' : 'totalPopUp'"
          @select-spread="handleSelectSpreadOrTotal"
          @close-dropdown="closeDropdown"
        />
      </div>
      <create-offer-modal
        v-if="offerMode"
        :full-game="fullGame"
        :session="session"
        :bid-ladder="bidLadder"
        :ask-ladder="askLadder"
        :spread-number="spread"
        :total-number="total"
        :alternative-totals="alternativeTotals"
        :alternative-spreads="alternativeSpreads"
        @return-to-parent-modal="returnToTakeBet"
        @close-modal="closeModal"
        @toggle-loading="toggleLoading"
        @add-width-to-bid-ladder="addWidthLadderArray"
        @offer-placed="phOutcome = 'offer_placed'"
      />
    </div>
    <select-spread-dropdown
      v-if="bidLadderSpread && ['total', 'spread'].includes(session.type)"
      v-show="session.type === 'spread' ? awaySpreadPopUp : totalPopUp"
      :game="fullGame"
      :bid-ladder-spread="bidLadderSpread"
      :left-elements="
        session.type === 'spread' ? alternativeSpreads.away : alternativeTotals
      "
      :right-elements="
        session.type === 'spread' ? alternativeSpreads.home : alternativeTotals
      "
      :popup="session.type === 'spread' ? 'awaySpreadPopUp' : 'totalPopUp'"
      @select-spread="handleSelectSpreadOrTotal"
      @close-dropdown="closeDropdown"
    />
  </modal>
  <modal v-else class="modal-wrap" @close-modal="closeModal">
    <spinner />
  </modal>
</template>

<script setup>
import CreateOfferModal from './CreateOfferModal.vue';
import InformationalTooltip from '@/components/global/tooltips/InformationalTooltip.vue';
import BidAskLadder from '../BidAskLadder.vue';
import AlertNotification from '@/components/betting/BetSlipAlert.vue';
import SelectSpreadDropdown from '@/components/betting/SelectSpreadDropdown.vue';
import Modal from '@/components/global/Modal.vue';
import Spinner from '@/components/global/Spinner.vue';
import SlideToConfirm from '@/components/global/SlideToConfirm.vue';
import WorldCupInjBadge from '@/components/global/WorldCupInjBadge.vue';
import ToAdvanceBadge from '@/components/global/ToAdvanceBadge.vue';
import dayjs from 'dayjs';
import { ref, computed, watch, onMounted, onBeforeUnmount, toRaw } from 'vue';
import { useStorage, onClickOutside } from '@vueuse/core';
import {
  getSideString,
  getOtherSideString,
  calculateWin,
  roundToFixed2,
  calculateRisk,
  calculateAverageOdds,
  noDecimal,
  getOtherSideType,
  currentDevice,
  roundInt,
  formatEventNameForDisplay,
  hasMustStartFlag,
  convertAmericanToImpliedProbability,
  convertImpliedProbabilityToAmerican,
  isWorldCupGame,
  slippageFloorOdds,
  isToAdvanceGame,
  getMarketSubtype,
} from '@/core/utils';
import { checkAuthAndShowLogin } from '@/core/authUtils';
import { getPeriodPrefix } from '@/consts/teamsDisplayNames';
import {
  formatBidAndAsk,
  getSideStringForModals,
  getBestOddsWithNumber,
} from '@/core/modalUtils';
import {
  oddsWithCommission,
  getRiskAmount,
  commissionAdjustedRisk,
  getBetPlusCommission,
  calculateTotalSumUntakenForNoVig,
  calculateWinForNoVig,
  rawSlippageForDisplayedTolerance,
} from '@/core/commissionUtils';
import { calculateRebateAmount, canSeeRebates } from '@/core/rebateUtils';
import { formatOdds, signOdds } from '@/core/oddsFormatUtils';
import {
  riskWinOptions,
  expiryItems,
  liveSlippageMaxPct,
} from '@/consts/constants';
import { useUserStore } from '@/stores/user';
import { useExchangeStore } from '@/stores/exchange';
import { usePlaceTrackerStore } from '@/stores/placeTracker';
import { PostHogUtils, resolveEmptyBalance } from '@/core/PostHogUtils';
import { useAPI, generateIdempotencyKey } from '@/composables/useAPI';
import {
  betTakenNotification,
  betTakenFailedNotification,
} from '@/core/notifications';
import { emitter } from '@/core/emitter';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons/faExclamationTriangle';
import { faBolt } from '@fortawesome/free-solid-svg-icons/faBolt';
import { faAngleUp } from '@fortawesome/free-solid-svg-icons/faAngleUp';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons/faAngleDown';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons/faArrowUp';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons/faCircleExclamation';
import { storeToRefs } from 'pinia';
import * as Sentry from '@sentry/vue';
import { useWindowSize } from '@/composables/useWindowSize';

const userStore = useUserStore();
const exchangeStore = useExchangeStore();
const placeTracker = usePlaceTrackerStore();
const { oddsComputed } = storeToRefs(exchangeStore);

const props = defineProps({
  session: {
    type: Object,
    required: true,
  },
  fullGame: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['closeModal']);

const hasMustStart = computed(() =>
  hasMustStartFlag(props.fullGame?.eventName),
);

const isSeries = computed(() =>
  (props.fullGame?.league ?? '').includes('SERIES'),
);

// World Cup games are graded on 90+INJ time only — surface the sticker.
const isWorldCup = computed(() => isWorldCupGame(props.fullGame));

// "To Advance" outright specials — surface the sticker instead of the raw
// "(To Advance)" mainPitcher text (which we suppress in the header).
const isToAdvance = computed(() => isToAdvanceGame(props.fullGame));

// Period token ("1H", "F5", "2H", "Q1" …) for 1st-half / first-5 style markets,
// derived from the participant longName. Empty for full-game markets. Shown as a
// pill in the header so the bettor clearly sees this isn't a full-game bet.
const periodLabel = computed(() =>
  getPeriodPrefix(props.fullGame?.participants?.[0]?.longName).trim(),
);

/**
 * Functionality for helpful computed values (Take Bet) (Create Offer)
 */
const betType = ref(props.session.type);
const sideType = computed(() => {
  if (props.session?.type === 'total') {
    return props.session.OU;
  } else if (
    props.session?.type === 'spread' ||
    props.session?.type === 'moneyline'
  ) {
    const participantI = props.fullGame.participants.findIndex(
      (el) => el.id === props.session.participantID,
    );
    const home = props.fullGame.participants[participantI].homeAway === 'home';
    if (props.session?.type === 'spread') {
      return home ? 'homeSpreads' : 'awaySpreads';
    }
    return home ? 'homeMoneylines' : 'awayMoneylines';
  } else if (props.session?.type === 'moneyline1x2') {
    if (props.session.market !== 'draw') {
      const participantI = props.fullGame.participants.findIndex(
        (el) => el.id === props.session.market,
      );
      const home =
        props.fullGame.participants[participantI].homeAway === 'home';
      if (home) {
        return { homeAwayDraw: 'homeMoneylines1x2', side: props.session.side };
      } else {
        return { homeAwayDraw: 'awayMoneylines1x2', side: props.session.side };
      }
    } else {
      return { homeAwayDraw: 'draw1x2', side: props.session.side };
    }
  }
});

const totalSumUntaken = computed(() => {
  const nonUserBets = bidLadder.value.filter(
    (bid) => bid.createdBy !== userStore.user.id,
  );
  return calculateTotalSumUntakenForNoVig(
    nonUserBets,
    props.fullGame.league,
    props.session.type,
  );
});
const available = computed(() => {
  return (
    parseFloat(userStore.user.displayBalance) +
    parseFloat(userStore.user.liability) -
    parseFloat(userStore.user.creditLimit)
  );
});
const maxTake = computed(() => {
  return Math.min(available.value, totalSumUntaken.value);
});
const maxButtonAmt = computed(() => {
  // First level the engine can actually fill — own resting orders never
  // fill this taker (spec-review F4, B-III class).
  return bidLadder.value.find((b) => b.createdBy !== userStore.user.id)
    ?.volume;
});

// PRO-94: Detect when user exceeds balance/maxLiability but can still bet max
const exceedsBalanceButCanBetMax = computed(() => {
  if (risk.value === '' || parseFloat(risk.value) < 1) return false;
  if (!validateZero.value) return false;
  if (available.value < 1) return false;

  const overBalance =
    available.value <
    getBetPlusCommission(
      risk.value,
      computedOdds.value,
      props.fullGame.league.toLowerCase(),
      props.session.type,
    );
  const overMaxLiability = parseFloat(risk.value) > (parseFloat(userStore.user.maxLiability) || Infinity);

  return overBalance || overMaxLiability;
});

// PRO-94: Calculate max risk for display (commission-adjusted)
const maxRiskForDisplay = computed(() => {
  const commission =
    Math.floor(available.value) * userStore.user.commissionCharged;
  const availableUpdated = userStore.user.viewOddsWithCommission
    ? Math.floor(available.value)
    : Math.floor(Math.floor(available.value) - commission);
  const maxOrderBook = Math.floor(totalSumUntaken.value * 100) / 100;

  let maxRisk;
  if (
    Math.floor(available.value * 100) / 100 >=
    Math.floor(maxOrderBook * 100) / 100
  ) {
    maxRisk = availableUpdated > maxOrderBook ? maxOrderBook : availableUpdated;
  } else {
    maxRisk = availableUpdated;
  }

  const maxLiab = parseFloat(userStore.user.maxLiability) || Infinity;
  return Math.min(maxRisk, maxLiab);
});

// PRO-94: Calculate blended win for the max risk amount
const maxWinForDisplay = computed(() => {
  if (maxRiskForDisplay.value <= 0) return 0;

  const bids = bidLadder.value.filter(
    (bid) => bid.createdBy !== userStore.user.id,
  );
  let valueUntaken = maxRiskForDisplay.value;
  let totalWin = 0;

  for (const bid of bids) {
    if (valueUntaken <= 0) break;
    const localValueToTake = Math.min(bid.volume, valueUntaken);
    totalWin += calculateWin(localValueToTake, bid.odds);
    valueUntaken -= localValueToTake;
  }

  return totalWin;
});

// PRO-94: Calculate blended odds for max risk amount (for header display)
const maxOddsForDisplay = computed(() => {
  if (maxRiskForDisplay.value <= 0) return computedOdds.value;

  const bids = bidLadder.value.filter(
    (bid) => bid.createdBy !== userStore.user.id,
  );
  let valueUntaken = maxRiskForDisplay.value;
  const selectedForMax = [];

  for (const bid of bids) {
    if (valueUntaken <= 0) break;
    const localValueToTake = Math.min(bid.volume, valueUntaken);
    selectedForMax.push({ ...bid, volume: localValueToTake });
    valueUntaken -= localValueToTake;
  }

  if (selectedForMax.length === 0) return computedOdds.value;

  const avgResult = oddsAverage(selectedForMax, maxRiskForDisplay.value);
  if (!avgResult) return computedOdds.value;
  return avgResult.avg || avgResult;
});

// PRO-94: Effective risk for ladder highlighting - uses max when over balance
const effectiveRiskForLadder = computed(() => {
  const riskNum = parseFloat(risk.value) || 0;
  if (riskNum < 1) return risk.value;
  
  const availNum = Number(available.value) || 0;
  const maxLiabNum = parseFloat(userStore.user.maxLiability) || Infinity;
  
  if (availNum <= 0) return risk.value;
  
  // 4casters: check commission when needed
  const betPlusComm = getBetPlusCommission(
    riskNum, 
    computedOdds.value, 
    props.fullGame.league.toLowerCase(), 
    props.session.type
  );
  const overBalance = availNum < betPlusComm;
  const overMaxLiability = riskNum > maxLiabNum;
  
  if (overBalance || overMaxLiability) {
    return maxRiskForDisplay.value.toString();
  }
  
  return risk.value;
});

const side = computed(() => {
  return {
    type: props.session.type,
    spread: props.session.spread,
    total: props.session.total,
    OU: props.session.OU,
    participantID: props.session.participantID,
  };
});

// ex Over 9.5 or Cleveland Gaurdians
const formattedSideString = computed(() => {
  let number = 0;
  if (betType.value === 'spread') {
    number = spread.value;
  } else if (betType.value === 'total') {
    number = total.value;
  }
  return getSideStringForModals(
    props.fullGame,
    props.session,
    betType.value,
    number,
  );
});

/**
 * Functionality for handling odds recalculations (Take Bet)
 */
const combinedOdds = ref(null);
const computedOdds = computed(() => {
  if (Number.isNaN(combinedOdds.value) || combinedOdds.value === null) {
    if (risk.value === '') {
      let number = betType.value === 'total' ? total.value : spread.value;
      return getBestOddsWithNumber(props.fullGame, sideType.value, number);
    }
    return props.session.odds;
  }
  return combinedOdds.value.toFixed(2);
});
const vigAdjustedOdds = computed(() => {
  return oddsWithCommission(
    computedOdds.value,
    props.fullGame.league.toLowerCase(),
    props.session.type,
    'precise',
  );
});

function recalcOdds(newRisk) {
  const avgOdds = oddsAverage(selectedBids.value, newRisk);
  if (!avgOdds) {
    handleOrderbookChangeDuringEntry();
    return false;
  }
  combinedOdds.value = avgOdds.avg;
  return true;
}

function oddsAverage(bids, risk) {
  let valueTaken = 0;
  let valueUntaken = risk;
  const data = bids.map((bid) => {
    const localValueToTake =
      bid.volume > valueUntaken ? valueUntaken : parseFloat(bid.volume);
    valueTaken += parseFloat(localValueToTake);
    valueUntaken -= parseFloat(localValueToTake);
    return {
      localValueToTake,
      valueUntaken,
      valueTaken,
      win: calculateWin(localValueToTake, bid.odds),
      odds: bid.odds,
      sideString: bid.sideString,
      type: bid.type,
      OU: bid.OU,
      spread: bid.spread,
      participantID: bid.participantID,
      total: bid.total,
    };
  });

  // occasionally errors hit due to data[0] not existing, I think because
  // bets can be taken and clear the board while the user is on the take bet modal
  if (!data[0]) return null;

  const sum =
    Number.parseFloat(risk) > 0
      ? data.reduce((acc, curr) => acc + curr.win, 0)
      : data[0].win;

  let avg;
  if (bids.length > 1) {
    avg = calculateAverageOdds(risk, sum);
  } else {
    avg = bids[0].odds;
  }
  return { avg, sum, data };
}

// Implied-probability gap (in percentage points) beyond which a worse fill
// level is considered "significantly different" from the best price taken.
const SLIPPAGE_IMPLIED_PROB_THRESHOLD = 5;
// A worse level must absorb more than this dollar amount (raw stake) before it
// is worth warning about — tiny crumbs at a bad price are not material.
const SLIPPAGE_MATERIALITY_FLOOR = 1;

// Detects when the user's stake would sweep into ladder levels whose price is
// materially worse than the best price they're taking. Returns null when there
// is nothing to warn about. Pure read of the current slip — never mutates.
function detectSlippage(bids, riskValue) {
  const riskNum = parseFloat(riskValue) || 0;
  if (riskNum < 1 || !bids || bids.length < 2) return null;

  const result = oddsAverage(bids, riskNum);
  if (!result || !result.data) return null;

  // Reference is the true top-of-book price being taken — the best price the
  // user sees. Use the first level with any positive fill, even if only a
  // crumb fills there, so a thin top level can't hide a worse material fill.
  const best = result.data.find((level) => level.localValueToTake > 0);
  if (!best) return null;
  const bestImpliedProb = convertAmericanToImpliedProbability(best.odds);

  // Warn about any level taking a material amount (> $1) at a price whose
  // implied probability is more than the threshold worse than top-of-book.
  const worseFills = result.data.filter(
    (level) =>
      level.localValueToTake > SLIPPAGE_MATERIALITY_FLOOR &&
      convertAmericanToImpliedProbability(level.odds) - bestImpliedProb >
        SLIPPAGE_IMPLIED_PROB_THRESHOLD,
  );
  if (!worseFills.length) return null;

  const league = props.fullGame.league.toLowerCase();
  const betTypeForDisplay = props.session.type;
  const displayRisk = (amount, odds) =>
    userStore.user.viewOddsWithCommission
      ? commissionAdjustedRisk(amount, odds, league, betTypeForDisplay)
      : amount;

  return {
    bestImpliedProb,
    bestOddsDisplay: formatOdds(best.odds, league, betTypeForDisplay, true),
    worseFills: worseFills.map((level) => ({
      odds: level.odds,
      impliedProb: convertAmericanToImpliedProbability(level.odds),
      amount: displayRisk(level.localValueToTake, level.odds),
      oddsDisplay: formatOdds(level.odds, league, betTypeForDisplay, true),
    })),
  };
}

/**
 * Functionality for handling risk and win updates from inputs or max button (Take Bet)
 */
const risk = ref('');
const win = ref('');

// --- Quick-amount selector ---------------------------------------------------
// Each field (risk / win) has a purple quick button that adds a fixed amount on
// tap (default +100). The amount is a user preference (configured in settings,
// persisted via useStorage); the dropdown does NOT change it — picking a value
// from the dropdown only sets the field value (and "Max" fills the max once).
// Mode is a numeric string or 'max' (settings may set it to 'max' so the button
// fills available instead of adding a fixed amount).
const riskQuickMode = useStorage('takebet-risk-quick-mode', '100');
const winQuickMode = useStorage('takebet-win-quick-mode', '100');
const riskDropdownRef = ref(null);
const winDropdownRef = ref(null);

function formatQuickLabel(mode) {
  if (mode === 'max') return 'Max';
  const n = Number(mode);
  return n % 1000 === 0 ? `+${n / 1000}K` : `+${n}`;
}
const riskQuickLabel = computed(() => formatQuickLabel(riskQuickMode.value));
const winQuickLabel = computed(() => formatQuickLabel(winQuickMode.value));

function handleRiskQuick() {
  if (!userStore.user.isLoggedIn) return;
  const isMax = riskQuickMode.value === 'max';
  PostHogUtils.capture('quick_add_used', {
    field: 'risk',
    amount: isMax ? null : Number(riskQuickMode.value),
    is_max: isMax,
    market_type: phMarketType.value,
    is_live: phIsLive.value,
  });
  if (isMax) {
    handleMaxButton();
    return;
  }
  const current = parseFloat(risk.value) || 0;
  const next = Math.round((current + Number(riskQuickMode.value)) * 100) / 100;
  handleRiskUpdate(next.toString());
}

function handleWinQuick() {
  if (!userStore.user.isLoggedIn) return;
  // Max fills the user's max risk (same as the dropdown's Max) and does not add
  // to the win amount, so it must not be gated by the sumUntaken guard below.
  if (winQuickMode.value === 'max') {
    PostHogUtils.capture('quick_add_used', {
      field: 'win',
      amount: null,
      is_max: true,
      market_type: phMarketType.value,
      is_live: phIsLive.value,
    });
    handleMaxButton();
    return;
  }
  // Adding to the win amount needs available book depth to fill into.
  if (props.session?.sumUntaken < 10) return;
  PostHogUtils.capture('quick_add_used', {
    field: 'win',
    amount: Number(winQuickMode.value),
    is_max: false,
    market_type: phMarketType.value,
    is_live: phIsLive.value,
  });
  const current = parseFloat(win.value) || 0;
  const next = Math.round((current + Number(winQuickMode.value)) * 100) / 100;
  handleWinUpdate(next.toString());
}
// -----------------------------------------------------------------------------

const updatingMaxRisk = ref(false);
const orderbookChangedDuringEntry = ref(false);
const orderbookChangeToastShown = ref(false);

function clearOrderbookChangedMessage() {
  orderbookChangedDuringEntry.value = false;
  orderbookChangeToastShown.value = false;
}

function handleOrderbookChangeDuringEntry() {
  // Bets may disappear while user is typing. Keep modal open and ask for reselection.
  orderbookChangedDuringEntry.value = true;
  if (!orderbookChangeToastShown.value) {
    betTakenFailedNotification({
      title: 'Odds Changed',
      detail: 'Odds changed while you were entering this bet. Please reselect from the ladder.',
    });
    orderbookChangeToastShown.value = true;
  }
  commitSelectedBids([]);
  combinedOdds.value = null;
  risk.value = '';
  win.value = '';
}

async function updateMaxRisk(newMax) {
  if (updatingMaxRisk.value) return;
  updatingMaxRisk.value = true;
  try {
    const { request } = useAPI('/user/updateMaxLiability');
    const response = await request({ maxLiability: newMax });
    if (response.success) {
      userStore.user.maxLiability = newMax;
    }
  } catch (err) {
    console.error('Failed to update max risk:', err);
  } finally {
    updatingMaxRisk.value = false;
  }
}

function handleRiskUpdate(newRisk) {
  clearOrderbookChangedMessage();
  if (!newRisk) {
    risk.value = '';
    combinedOdds.value = null;
    commitSelectedBids([]);
    win.value = '';
    return;
  }

  risk.value = newRisk;
  let floatRisk = parseFloat(newRisk);

  if (floatRisk > totalSumUntaken.value) {
    risk.value = totalSumUntaken.value.toFixed(2);
    floatRisk = totalSumUntaken.value;
  }

  selectBids(floatRisk);
  const recalculated = recalcOdds(floatRisk);
  if (!recalculated) return;
  win.value = calculateWin(floatRisk, vigAdjustedOdds.value).toFixed(2);
}

function handleRiskDropdown(item) {
  // ✅ Check authentication first
  if (!userStore.user.isLoggedIn) {
    return;
  }
  dropdownRisk.value = false;
  // The dropdown only sets the field value — it does NOT change the quick
  // button's amount (that's configured in user settings, default 100).
  if (item === 'max') {
    handleMaxButton();
    return;
  }
  handleRiskUpdate(item);
}

function handleWinUpdate(newWin) {
  clearOrderbookChangedMessage();
  win.value = newWin;
  const floatWin = parseFloat(newWin);
  const updatedFromWin = setBidsAndOddsFromWin(floatWin);
  if (!updatedFromWin) return;
  const newRisk = calculateRisk(floatWin, vigAdjustedOdds.value).toFixed(2);
  if (newRisk > totalSumUntaken.value) {
    handleRiskUpdate(newRisk);
    return;
  }
  risk.value = calculateRisk(floatWin, vigAdjustedOdds.value).toFixed(2);
  recalcOdds(risk.value);
}

function handleWinDropdown(item) {
  // ✅ Check authentication first
  if (!userStore.user.isLoggedIn) {
    return;
  }
  dropdownWin.value = false;
  // The dropdown only sets the field value — it does NOT change the quick
  // button's amount (that's configured in user settings, default 100).
  if (item === 'max') {
    handleMaxButton();
    return;
  }
  handleWinUpdate(item);
}

function handleMaxButton() {
  // ✅ Check authentication first
  if (!userStore.user.isLoggedIn) {
    return;
  }
  // Nothing fillable — empty book or only the user's own resting orders
  // (maxButtonAmt is undefined then, and NaN comparisons below would fall
  // through to sizing from raw balance before tripping the misleading
  // "orderbook changed" message). A max of nothing is a no-op (bugbot).
  if (!(Number(maxButtonAmt.value) > 0)) {
    return;
  }
  Sentry.addBreadcrumb({
    category: 'takebet-modal',
    message: `TAKEBET MODAL - ${userStore.user.username} clicked Max button, side ${formattedSideString.value}`,
    level: 'info',
  });
  clearOrderbookChangedMessage();
  const commission =
    Math.floor(available.value) * userStore.user.commissionCharged;
  const availableUpdated = userStore.user.viewOddsWithCommission
    ? Math.floor(available.value)
    : Math.floor(Math.floor(available.value) - commission);
  if (
    Math.floor(available.value * 100) / 100 >=
    Math.floor(maxButtonAmt.value * 100) / 100
  ) {
    risk.value =
      availableUpdated > (Math.floor(maxButtonAmt.value * 100) / 100).toFixed(2)
        ? (Math.floor(maxButtonAmt.value * 100) / 100).toFixed(2)
        : availableUpdated.toString();
    risk.value =
      risk.value < userStore.user.maxLiability
        ? risk.value
        : userStore.user.maxLiability.toString();
    selectBids(risk.value);
    const recalculated = recalcOdds(risk.value);
    if (!recalculated) return;
    win.value = calculateWin(risk.value, vigAdjustedOdds.value).toFixed(2);
  } else {
    risk.value = availableUpdated.toString();
    risk.value =
      risk.value < userStore.user.maxLiability
        ? risk.value
        : userStore.user.maxLiability.toString();
    selectBids(risk.value);
    const recalculated = recalcOdds(risk.value);
    if (!recalculated) return;
    win.value = calculateWin(risk.value, vigAdjustedOdds.value).toFixed(2);
  }
}

function setBidsAndOddsFromWin(newWin) {
  let leftToFill = newWin;
  let totalRisk = 0;
  const newSelectedBids = [];
  let shouldStop = false;
  let i = 0;
  while (i < bidLadder.value.length && !shouldStop) {
    const bid = bidLadder.value[i];
    // Skip own resting orders — the engine can never fill them for this
    // taker (spec-review B-III).
    if (bid.createdBy === userStore.user.id) {
      i++;
      continue;
    }
    const maxWin = calculateWin(bid.volume, bid.odds);
    newSelectedBids.push(bid);
    if (leftToFill > maxWin) {
      leftToFill -= maxWin;
      totalRisk += bid.volume;
    } else {
      totalRisk += calculateRisk(leftToFill, bid.odds);
      shouldStop = true;
    }
    i++;
  }

  commitSelectedBids(newSelectedBids);
  const oddsAvg = oddsAverage(newSelectedBids, totalRisk);
  if (!oddsAvg) {
    handleOrderbookChangeDuringEntry();
    return false;
  }
  combinedOdds.value = oddsAvg.avg;
  risk.value = totalRisk.toFixed(2);
  return true;
}

/***
 * Functionality related to selected bids (Take Bet)
 */
const selectedBids = ref([]);
// Consent anchor: the worst selected level's odds, snapshotted BY VALUE at
// selection time. selectedBids holds references to live ladder objects whose
// odds the price stream mutates in place — reading them at submit time sent
// an ask the user never consented to (QA critical: slip showed −110, order
// carried −128, and the slippage floor compounded from the moved ask). Every
// selectedBids assignment goes through this setter so the snapshot can never
// be skipped.
const committedWorstOdds = ref(null);
// True while the re-book prompt is open: suppresses the live-delay banner
// without releasing `loading` (which must keep the Take button disabled so a
// second placement can't race the re-book decision).
const rebookPromptOpen = ref(false);
// Build fingerprint for QA log-gating: proves which consent-anchor revision
// the served bundle contains (stale-bundle/wrong-checkout runs are
// indistinguishable from code bugs without it).
const PRO234_BUILD = 'consent-v30-exact-floor-display';
function commitSelectedBids(bids) {
  selectedBids.value = bids;
  committedWorstOdds.value = bids.length
    ? Number(bids[bids.length - 1].odds)
    : null;
}
// Worst price the CURRENT ladder would sweep for the given stake — used only
// for drift telemetry at submit (never for the order itself).
function currentLadderWorstFor(stake) {
  let sum = 0;
  let worst = null;
  for (const bid of bidLadder.value) {
    if (bid.createdBy === userStore.user.id) continue;
    if (sum >= stake) break;
    sum += bid.volume;
    worst = Number(bid.odds);
  }
  return worst;
}
const selectedBidIds = computed(() => {
  return selectedBids.value.map((bid) => bid.id);
});

// receives an array of the selected bid (offer) objects from bid ask ladder
function handleSelectBids(selectBidObjs) {
  // ✅ Check authentication first
  if (!userStore.user.isLoggedIn) {
    return;
  }
  clearOrderbookChangedMessage();
  const filteredBids = selectBidObjs.filter(
    (b) => b.createdBy !== userStore.user.id,
  );
  // Clicking only your own rows selects nothing fillable — a silent no-op,
  // not an "Odds Changed" toast (nothing changed; spec-review F5).
  if (!filteredBids.length) return;
  // Commit the FILTERED selection: risk/win are priced from it, and the
  // snapshot must be the worst price the slip actually showed — an own order
  // as the deepest row otherwise committed a price the numbers never
  // reflected (spec-review B-III).
  commitSelectedBids(filteredBids);
  let newRisk = getRiskWhenClick(filteredBids);
  const avgOdds = oddsAverage(filteredBids, newRisk);
  if (!avgOdds) {
    handleOrderbookChangeDuringEntry();
    return;
  }
  combinedOdds.value = avgOdds.avg;
  win.value = calculateWin(newRisk, vigAdjustedOdds.value).toFixed(2);
  risk.value = newRisk.toFixed(2);
}

function selectBids(newRisk) {
  const newSelectedBids = [];
  let sum = 0;
  let i = 0;
  // `sum < newRisk` (strict): with `<=`, a risk exactly equal to the swept
  // levels' volume pushed one more ZERO-FILL level, and the consent snapshot
  // committed THAT level's odds — the slip showed the real levels' price but
  // the order asked the deeper level's (QA release-blocker / spec-review
  // B-II). The strict comparison still covers the max-button rounding case
  // (risk a fraction of a cent above sum → next level still pushed).
  while (sum < newRisk) {
    if (!bidLadder.value[i]) break;
    // Own resting orders can't fill this take (the engine skips self-matches)
    // — sweeping their volume put prices in the projection and snapshot that
    // the engine can never fill (spec-review B-III).
    if (bidLadder.value[i].createdBy !== userStore.user.id) {
      sum += bidLadder.value[i].volume;
      newSelectedBids.push(bidLadder.value[i]);
    }
    i++;
  }
  commitSelectedBids(newSelectedBids);
}

function addWidthLadderArray(arrayLadder) {
  bidLadder.value.forEach((value, index) => {
    bidLadder.value[index]['widthLadder'] = arrayLadder[index] ?? '0%';
  });
}

function getRiskWhenClick(bids) {
  // let valueUntaken = userStore.user.available;
  let valueUntaken =
    parseFloat(userStore.user.displayBalance) +
    parseFloat(userStore.user.liability) -
    parseFloat(userStore.user.creditLimit);
  let valueTaken = 0;
  if (Number.parseFloat(valueUntaken) > 0) {
    bids.forEach((bid) => {
      const localValueToTake =
        bid.volume > valueUntaken ? valueUntaken : parseFloat(bid.volume);
      valueTaken += parseFloat(localValueToTake);
      valueUntaken -= parseFloat(localValueToTake);
    });
  }
  return valueTaken;
}

/**
 * Functionality to take a bet (Take Bet)
 */
// Titles for explicit exchange rejections (errorType comes from orders-api's
// place status). Only these types carry user-facing `error` messages we can
// show verbatim; anything else (network failures, raw orders-api HTTP error
// bodies) falls back to the generic unavailable copy.
const PLACE_REJECTION_TITLES = {
  rejected_liability: 'Insufficient Balance',
  rejected_betting_not_allowed: 'Betting Closed',
  rejected_order_too_small: 'Take Order Failed',
  rejected_tick_size_rules: 'Take Order Failed',
  rejected_order_type_rules: 'Take Order Failed',
  validation_error: 'Take Order Failed',
};

function placeRejectionNotification(rejection) {
  const title = PLACE_REJECTION_TITLES[rejection.errorType];
  if (title && typeof rejection.error === 'string' && rejection.error) {
    return { title, detail: rejection.error };
  }
  return {
    title: 'Take Order Failed',
    detail: 'This order is no longer available.',
  };
}

function betTakenFailed(element) {
  closeModal();
  Sentry.addBreadcrumb({
    category: 'function',
    message: 'Input to bet taken failed',
    level: 'warning',
    data: {
      response: element,
    },
  });
  Sentry.captureMessage(`Bet Taken Failed`, {
    level: 'error',
  });
  if (!element.error) {
    let notifElement = element;
    if (element.message) {
      notifElement = element.message;
    }
    let data = {
      title: 'Error Taking Bet',
      detail: 'An Unknown Error Occured, Please Try Again',
    };
    if (typeof notifElement === 'string' && notifElement.includes('locked')) {
      data = {
        title: 'Account Paused',
        detail: 'Please contact an admin to unlock your account',
      };
      betTakenFailedNotification(data);
    } else if (
      typeof notifElement === 'string' &&
      notifElement.includes('USA')
    ) {
      data = {
        title: 'Bet Taken Failed',
        detail: 'Access to this service is not available in the USA',
      };
      betTakenFailedNotification(data);
    } else {
      betTakenFailedNotification(data);
    }
  } else {
    betTakenFailedNotification(element.error);
  }
}

// PRO-94: Take bet at max available amount
async function takeBetAtMax() {
  if (loading.value) {
    // Guard fired = a duplicate tap actually reached the handler. Decision:
    // is the double-submit guard doing real work on real users, or is it
    // dead weight (and if it over-fires, it's silently eating legit bets)?
    PostHogUtils.capture('duplicate_submit_blocked', { surface: 'take' });
    return;
  }
  Sentry.addBreadcrumb({
    category: 'takebet-modal',
    message: `TAKEBET MODAL - ${userStore.user.username} submitting at max (exceeds balance path), side ${formattedSideString.value}, maxRisk ${maxRiskForDisplay.value}`,
    level: 'info',
  });
  clearOrderbookChangedMessage();
  const maxRisk = maxRiskForDisplay.value;
  // Max click is fresh consent: the button's numbers, this reselect, and the
  // commit all derive from the same-tick ladder, so displayed == committed ==
  // submitted (the earlier guard against the typed-risk commit spuriously
  // aborted every over-balance max-take — the max sweep is shallower by
  // construction; spec-review F1).
  risk.value = maxRisk.toFixed(2);
  selectBids(maxRisk);
  const recalculated = recalcOdds(maxRisk);
  if (!recalculated) return;
  win.value = calculateWin(maxRisk, vigAdjustedOdds.value).toFixed(2);
  await takeBet();
}

// When any live bet fills nothing (price moved past the asked price — or past
// the slippage floor for opted-in users), surface the fresh price and let the
// user re-book at it in one tap. Returns true if the miss was handled here
// (offered/declined/re-booked), false if there's no live price to offer — in
// which case the caller shows the normal "no longer available" failure.
// ONE floor pipeline for the card display AND the wire ask (they must never
// diverge — spec-review 25 verified the shared pipeline; the dodge lives
// inside it). Own-order dodge (G1, PM decision 2026-07-20, backend
// untouchable): the engine cancels the taker's OWN resting orders it meets
// while matching (existing self-cross behavior); the widened window could
// walk into own orders the displayed price never would. The floor stops one
// hundredth of a point better than the user's own BEST-priced order inside
// the window — the engine's RoundToBetterTick lifts that to the next real
// tick — so matching never reaches any own order. Cost: tolerance truncates
// at your own price (same-tick strangers excluded too); the card DISCLOSES
// the truncation (PM-approved copy). At-ask own orders keep today's
// strict-take behavior. Residual: own orders not in this tab's ladder
// (another device, mid-flight) cannot be dodged. Tightening only — the
// wire floor is never wider than the displayed promise.
function effectiveFloorFor(anchorRaw, rawPts) {
  let floor = rawPts > 0 ? slippageFloorOdds(anchorRaw, rawPts) : anchorRaw;
  let ownOdds = null;
  if (rawPts > 0) {
    const pAsk = convertAmericanToImpliedProbability(anchorRaw);
    const pFloor = convertAmericanToImpliedProbability(floor);
    let bindingP = Infinity;
    for (const bid of bidLadder.value) {
      if (bid.createdBy !== userStore.user.id) continue;
      const odds = Number(bid.odds);
      if (!Number.isFinite(odds)) continue;
      const p = convertAmericanToImpliedProbability(odds);
      // Strictly worse than the ask, at-or-better than the floor: exactly
      // the region only the widening exposes.
      if (p > pAsk + 1e-9 && p <= pFloor + 1e-9 && p < bindingP) {
        bindingP = p;
        ownOdds = odds;
      }
    }
    if (ownOdds !== null) {
      // Set the floor a hair BETTER (lower implied prob) than the own order so
      // the engine's matching never reaches it. Compute in PROBABILITY space
      // and convert back: "ownOdds + 0.01" in American-odds space crosses the
      // invalid (−100, +100) gap when the own order sits at even money
      // (−100 + 0.01 → −99.9, which master rejects). prob→odds always lands on
      // a valid side of even money, and gives the identical result away from it.
      const pOwn = convertAmericanToImpliedProbability(ownOdds);
      floor = convertImpliedProbabilityToAmerican(Math.max(0, pOwn - 0.01));
    }
    // Master validation REJECTS odds with more than one decimal place
    // ("american odds must have at most one decimal place") — it does NOT
    // round for us (SC3K v28 smoke: the raw float floor made every
    // slippage take unbookable; −119.2 books, −119.20668… is refused).
    // Round bettor-favorably (numerically up) to one decimal: the
    // effective floor only ever TIGHTENS, and a dodge boundary of
    // ownOdds+0.01 stays strictly better than the own order.
    floor = Math.ceil(floor * 10) / 10;
  }
  return { floor, ownOdds };
}
// False once this slip instance is unmounting: the re-book prompt must
// neither open over an unrelated page from a dead slip's closure state nor
// let a late confirm book from it (spec-review 20 M5).
let slipAlive = true;
// The blended price the user was staring at when they hit submit — the
// Asked row's odds must cohere with the slip's risk/win pair, not show the
// deepest swept level next to full-stake numbers (spec-review 21 M14).
const lastSubmitProjectionOdds = ref(null);
async function offerRebookAtNewPrice(slippageExceeded = false) {
  // Analytics: why this prompt fired — carried on all three rebook events so
  // accept rates can be split by trigger (price moved vs slippage exceeded).
  const rebookTrigger = slippageExceeded ? 'slippage_exceeded' : 'price_moved';
  // PM design decision (2026-07-03, supersedes the M2 "strictly worse"
  // guard): the modal's in-memory ladder is BY CONSTRUCTION the snapshot the
  // user tapped — its best is the failed ask, so any "did it move?"
  // comparison against it answers "no" forever; the prompt never fired in
  // any environment. Ask the SERVER for the current book instead, and offer
  // whatever best non-self price exists — worse, equal, or better.
  let freshLadder;
  try {
    const { request } = useAPI('/exchange/getSingleOrderbook');
    const resp = await request({ gameID: props.fullGame.id });
    if (!resp?.game) return false;
    freshLadder = ladderFromGame(resp.game);
  } catch {
    // Fetch failure = nothing to offer -> caller shows the plain failure.
    return false;
  }
  const freshBest = freshLadder?.find(
    (bid) => bid.createdBy !== userStore.user.id,
  );
  if (!freshBest) return false;
  const offeredOdds = Number(freshBest.odds);
  if (!Number.isFinite(offeredOdds)) return false;

  // The offered wager is min(wanted, available at the offered price): never
  // depict a stake the book cannot hold (PM). "Available" is the summed
  // non-self volume at the offered price — nothing better exists or it would
  // be the best — so the trimmed wager books whole at the pinned price.
  const wantedRisk = parseFloat(risk.value) || 0;
  if (!(wantedRisk > 0)) return false;
  const available = freshLadder
    .filter(
      (bid) =>
        bid.createdBy !== userStore.user.id &&
        Number(bid.odds) === offeredOdds,
    )
    .reduce((sum, bid) => sum + (Number(bid.volume) || 0), 0);
  if (!(available > 0)) return false;
  const rebookRisk = Math.min(wantedRisk, available);

  const league = props.fullGame.league.toLowerCase();
  const orderType = props.session.type;
  const rebookWin = calculateWin(
    rebookRisk,
    oddsWithCommission(offeredOdds, league, orderType, 'precise'),
  );
  const gameInfo =
    props.fullGame.participants && props.fullGame.participants.length >= 2
      ? `${props.fullGame.participants[0].longName} vs ${props.fullGame.participants[1].longName}`
      : 'Game Info Not Available';

  // Dead slip = no prompt: the modal would open over whatever page the user
  // is on now, offering a bet from stale closure state (spec-review 20 M5).
  // The bail runs BEFORE any analytics (carried R1): the caller's plain-
  // failure path records its own event, and firing here too double-counted
  // every dead-slip miss.
  if (!slipAlive) return false;

  trackLiveBetResult(
    'failed',
    slippageExceeded ? 'slippage_exceeded' : 'price_moved',
  );

  // The Asked row must cohere as a row: odds = the blended price displayed
  // at submit (matches the slip's risk/win math), not the deepest swept
  // level (spec-review 21 M14). Fallback to the committed snapshot.
  const askedOddsRaw = Number.isFinite(lastSubmitProjectionOdds.value)
    ? lastSubmitProjectionOdds.value
    : committedWorstOdds.value;
  // Style the odds change honestly: the server fetch can return a BETTER
  // price than asked — red + "no longer available" is wrong then
  // (spec-review 21 L). Worse for the bettor = higher implied probability.
  const offeredWorse =
    convertAmericanToImpliedProbability(offeredOdds) >
    convertAmericanToImpliedProbability(askedOddsRaw) + 1e-9;

  rebookPromptOpen.value = true;
  let autoDeclineReason = null;
  const confirmed = await new Promise((resolve) => {
    // The prompt must ALWAYS settle (bugbot): if no mounted listener shows
    // the modal — layout unmounted, listener not wired in the served
    // bundle — the await would otherwise hang forever with the slip frozen
    // in loading. The modal acks via onShown; no ack within 3s = no prompt
    // ever appeared → settle as declined and fail plainly. settle() is
    // idempotent so a late ack/confirm can't double-resolve.
    let settled = false;
    let shown = false;
    let noListenerTimer = null;
    const settle = (value) => {
      if (settled) return;
      settled = true;
      if (noListenerTimer) clearTimeout(noListenerTimer);
      resolve(value);
    };
    noListenerTimer = setTimeout(() => {
      if (!shown) {
        autoDeclineReason = 'no_prompt_rendered';
        settle(false);
      }
    }, 3000);
    emitter.emit('showPriceMovedModal', {
      gameInfo,
      sideString: formattedSideString.value,
      askedOddsDisplay: formatOdds(askedOddsRaw, league, orderType, true),
      offeredOddsDisplay: formatOdds(offeredOdds, league, orderType, true),
      askedRisk: wantedRisk,
      offeredRisk: rebookRisk,
      askedWin: parseFloat(win.value) || 0,
      offeredWin: rebookWin,
      available,
      offeredWorse,
      onShown: () => {
        shown = true;
        // Offered = the prompt actually rendered — firing pre-emit counted
        // offers that never showed (spec-review 22 LOW).
        PostHogUtils.capture('slippage_rebook_offered', {
          game_id: props.fullGame.id,
          side: formattedSideString.value,
          newOdds: offeredOdds,
          // Same blended price the prompt's Asked row shows — on multi-level
          // sweeps this diverges from the committed worst level, and analytics
          // must match what the user saw (bugbot).
          asked_odds: askedOddsRaw,
          reason: rebookTrigger,
          market_type: phMarketType.value,
        });
      },
      onConfirm: () => {
        if (!slipAlive) {
          // Slip closed while the prompt was up — do not book from a dead
          // instance's state (spec-review 20 M5).
          autoDeclineReason = 'slip_closed';
          settle(false);
          return;
        }
        settle(true);
      },
      onCancel: (reason) => {
        if (typeof reason === 'string') autoDeclineReason = reason;
        settle(false);
      },
    });
  });
  rebookPromptOpen.value = false;

  if (!confirmed) {
    // An auto-decline is a FAILURE the user never saw — "fail plainly"
    // means saying so, not silently closing (spec-review 20 M3); and the
    // analytics must not count a prompt that never rendered as a user
    // decline.
    if (autoDeclineReason) {
      betTakenFailedNotification('This order is no longer available');
    }
    PostHogUtils.capture('slippage_rebook_declined', {
      game_id: props.fullGame.id,
      side: formattedSideString.value,
      auto_reason: autoDeclineReason || 'user',
      asked_odds: askedOddsRaw,
      reason: rebookTrigger,
      market_type: phMarketType.value,
    });
    closeModal();
    return true;
  }

  PostHogUtils.capture('slippage_rebook_confirmed', {
    game_id: props.fullGame.id,
    side: formattedSideString.value,
    asked_odds: askedOddsRaw,
    reason: rebookTrigger,
    market_type: phMarketType.value,
  });

  // Pin the consent: the order asks exactly the price and stake the user just
  // confirmed. The engine's $gte guarantees nothing books worse than the
  // pinned price (better still fills), and if it moved again during the
  // prompt the strict order simply no-fills — with no second prompt
  // (forceStrict). The slip's numbers are set to the confirmed values so
  // sizing, disclosure, and the order all agree.
  risk.value = rebookRisk.toFixed(2);
  win.value = rebookWin.toFixed(2);
  await takeBet(true, true, offeredOdds);
  return true;
}

async function takeBet(
  skipSlippageCheck = false,
  forceStrict = false,
  pinnedWorstOdds = null,
) {
  // Synchronous re-entrancy guard. The button's :disabled only takes effect on
  // the next render, so taps queued while the main thread is busy each reach
  // this handler (2026-07-12: a user's take executed three times this way).
  // EXCEPTION: the pinned re-book (forceStrict + a pinned price) legitimately
  // re-enters while the OUTER placement still holds `loading` (it stays true
  // through the re-book prompt to block a racing tap). Let that continuation
  // through — it runs synchronously up to the network call, so no tap can race
  // in — and block only genuine duplicate taps (which never carry a pin).
  const isPinnedRebook = forceStrict && Number.isFinite(pinnedWorstOdds);
  if (loading.value && !isPinnedRebook) {
    // See takeBetAtMax — is the guard firing on real users?
    PostHogUtils.capture('duplicate_submit_blocked', { surface: 'take' });
    return;
  }
  // Pre-placement slippage gate: if the stake would sweep into a materially
  // worse price than the best one being taken, pause and confirm with the user
  // before sending anything. Confirming re-enters with the check skipped.
  if (!skipSlippageCheck) {
    const slippage = detectSlippage(selectedBids.value, risk.value);
    if (slippage) {
      const worst = slippage.worseFills[slippage.worseFills.length - 1];
      PostHogUtils.capture('slippage_warning_shown', {
        gameID: props.fullGame.id,
        side: formattedSideString.value,
        bestImpliedProb: slippage.bestImpliedProb,
        worstImpliedProb: worst?.impliedProb,
        impliedProbGap: (worst?.impliedProb ?? 0) - slippage.bestImpliedProb,
      });
      emitter.emit('showSlippageConfirmModal', {
        bestOddsDisplay: slippage.bestOddsDisplay,
        worseFills: slippage.worseFills.map((fill) => ({
          oddsDisplay: fill.oddsDisplay,
          amount: fill.amount,
        })),
        onConfirm: () => {
          PostHogUtils.capture('slippage_warning_confirmed', {
            gameID: props.fullGame.id,
            side: formattedSideString.value,
          });
          takeBet(true);
        },
        onCancel: () => {
          PostHogUtils.capture('slippage_warning_cancelled', {
            gameID: props.fullGame.id,
            side: formattedSideString.value,
          });
        },
      });
      return;
    }
  }
  loading.value = true;
  trackLiveBetAttempt();
  // Register past the slippage gate so http_ms covers the real commit; finally reports the HTTP result.
  const placeAttemptId = placeTracker.register({
    orderType: 'fillAndKill',
    gameId: props.fullGame.id,
    marketType: phMarketType.value,
    isLive: phIsLive.value,
    leagueId: props.fullGame?.league ?? null,
    sportId: props.fullGame?.sport ?? null,
  });
  phPlaceAttemptId.value = placeAttemptId;
  let httpStatus = 'rejected';
  // Place response legs for exact order-ID side-update matching (matched[].orderID).
  let placeSessionsForTelemetry = null;
  try {
    // The submitted ask is ALWAYS a consented price: either the re-book pin
    // or the selection-time snapshot — never a live read of selectedBids,
    // whose ladder-object refs the price stream mutates in place (QA
    // critical: consented -110, submitted -128).
    const worstOdds = Number.isFinite(pinnedWorstOdds)
      ? pinnedWorstOdds
      : committedWorstOdds.value;
    if (!Number.isFinite(worstOdds)) {
      betTakenFailedNotification({
        title: 'Take Order Failed',
        detail: 'This order is no longer available.',
      });
      trackLiveBetResult('failed', 'order_unavailable');
      closeModal();
      return;
    }
    // Live slippage — 100% FRONTEND design (CTO directive 2026-07-17: the
    // matching engine is untouched). Instead of sending a tolerance for the
    // server to widen with, the ORDER ITSELF asks for the floor price: the
    // engine's normal $gte fills best-first at makers' real odds, so if the
    // displayed price is still there the user gets it, and worse levels only
    // fill down to the floor. fillAndKill = nothing ever rests at the floor.
    // Pre-game, strict mode, dial 0, and the pinned re-book path all submit
    // the displayed price verbatim — byte-identical to today.
    // Gate on the DIAL, not the store mode: mid-drag (@input before @change
    // persists) the store can lag the card — the order must match what the
    // UI shows (bugbot).
    const useSlippage =
      props.fullGame.live && Number(betSlippagePct.value) > 0 && !forceStrict;
    const slippagePct = useSlippage
      ? Math.min(liveSlippageMaxPct, Number(betSlippagePct.value) || 0)
      : 0;
    // The setting is a promise on the odds scale the user sees. For
    // commission-view users, convert it to raw engine points exact for this
    // price (shrink-only), so the realized slip on their displayed scale can
    // never exceed the configured tolerance. Identity for everyone else.
    const sentSlippagePct = rawSlippageForDisplayedTolerance(
      worstOdds,
      slippagePct,
      props.fullGame.league.toLowerCase(),
      props.session.type,
    );
    // The wire ask: floor(displayed ask, tolerance) when slippage is active,
    // the displayed ask verbatim otherwise. The engine tick-rounds fractional
    // floors BETTOR-FAVORABLY (ticks.RoundToBetterTick — first tick ≥ odds),
    // so the effective floor is never wider than the one displayed.
    const { floor: effectiveFloorOdds } = effectiveFloorFor(
      worstOdds,
      sentSlippagePct,
    );
    const wireAskOdds = effectiveFloorOdds;
    const PLACE_ROUTE = 'session/v2/place/';
    const { request } = useAPI(PLACE_ROUTE);
    const riskAmt = getRiskAmount(
      risk.value,
      win.value,
      worstOdds,
      props.fullGame.league.toLowerCase(),
      betType.value,
    );
    const order = {
      gameID: props.fullGame.id,
      odds: wireAskOdds,
      bet: riskAmt,
      type: props.session.type,
      number: null,
      side:
        props.session.type === 'total'
          ? props.session.OU
          : props.session.participantID,
      expirationMinutes: 1,
      orderType: 'fillAndKill',
    };

    if (order.type === 'total') {
      order.number = total.value;
    } else if (order.type === 'spread') {
      order.number = spread.value;
    } else if (props.session.type === 'moneyline1x2') {
      order.side = props.session.side;
      order.market = props.session.market;
    }

    const payload = [order];

    // Snapshot what the slip projected at placement time so we can compare it
    // against the actual fill once the response lands. These are exactly the
    // numbers the user saw in the slip — no inferred "intended" amount, which
    // could go stale relative to the stake actually being placed. On a pinned
    // re-book the "wanted" price is the consented one, not the pre-prompt
    // slip's computedOdds, which reflects the ladder before the price moved.
    const league = props.fullGame.league.toLowerCase();
    const orderType = props.session.type;
    const wantedRisk = parseFloat(risk.value) || 0;
    const wantedWin = parseFloat(win.value) || 0;
    const wantedOddsRaw = Number.isFinite(pinnedWorstOdds)
      ? pinnedWorstOdds
      : parseFloat(computedOdds.value);
    const wantedOddsDisplay = formatOdds(
      wantedOddsRaw,
      league,
      orderType,
      true,
    );

    // Whether the final attempt's response carried any explicit rejections;
    // the post-loop re-book offer is for pure no-match outcomes only.
    let lastAttemptHadRejections = false;

    // Submit-time consent invariant (QA gate), frontend-only form: the wire
    // ask must equal floor(committed snapshot, tolerance) — or the snapshot
    // itself when strict — never a live ladder re-read. Drift is measured
    // against the CURRENT ladder's worst for the same stake — selectedBids
    // holds detached clones and can never diverge from its own snapshot
    // (spec-review F2).
    const ladderWorstNow = currentLadderWorstFor(parseFloat(risk.value) || 0);
    // Snapshot the displayed blended price for the re-book prompt's Asked
    // row (spec-review 21 M14) — by prompt time the reactive computed may
    // have moved with the book.
    lastSubmitProjectionOdds.value = parseFloat(computedOdds.value);
    Sentry.addBreadcrumb({
      category: 'takebet-modal',
      message: `[PRO234] ${PRO234_BUILD} submit`,
      level: 'info',
      data: {
        // What the engine receives: the floored ask (== displayedAskOdds
        // when strict).
        orderOdds: wireAskOdds,
        displayedAskOdds: worstOdds,
        committedWorstOdds: committedWorstOdds.value,
        pinnedWorstOdds,
        projectionOdds: parseFloat(computedOdds.value),
        ladderWorstNow,
        sentSlippagePct,
        configuredPct: Math.min(
          liveSlippageMaxPct,
          Number(userStore.user.liveSlippagePct) || 0,
        ),
        perBetPct: slippagePct,
      },
    });
    if (
      Number.isFinite(ladderWorstNow) &&
      ladderWorstNow !== worstOdds &&
      !Number.isFinite(pinnedWorstOdds)
    ) {
      PostHogUtils.capture('consent_drift_detected', {
        order_odds: worstOdds,
        current_ladder_worst: ladderWorstNow,
        build: PRO234_BUILD,
      });
    }

    // One silent retry catches the rebuilt orderbook when the line moved —
    // live: during go-api's delayIfLive window; prematch: another taker
    // beat us to it. Backend's $gte matching auto-fills at the new best if
    // it's still >= asked.
    const MAX_ATTEMPTS = 2;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      Sentry.addBreadcrumb({
        category: 'takebet-modal',
        message: `TAKEBET MODAL: sending takebet to api (attempt ${attempt}/${MAX_ATTEMPTS})`,
        level: 'info',
        data: {
          username: userStore.user.username,
          side: formattedSideString.value,
          odds: worstOdds,
          risk: riskAmt,
          win: win.value,
        },
      });
      const response = await request(
        {
          orders: payload,
        },
        null,
        { idempotencyKey: `${placeIntentKey}:${attempt}` },
      );

      const sessions = response?.createdSessions ?? [];

      if (!sessions.length) {
        // Empty response = the order never reached the engine (orders-api
        // unreachable) — an infrastructure failure, not a price move. A
        // re-book offer here would just fail the same way, so fail plainly.
        betTakenFailedNotification({
          title: 'Take Order Failed',
          detail: 'This order is no longer available.',
        });
        trackLiveBetResult('failed', 'order_unavailable');
        closeModal();
        return;
      }

      // Every order explicitly rejected by the exchange (insufficient
      // balance, betting closed, etc.). The outcome is deterministic, so
      // retrying is pointless — surface the real reason instead of the
      // generic "no longer available" copy.

      // Engine no-fill is NOT a deterministic rejection — it is the price-moved
      // case the re-book prompt exists for. Carve it out of the rejection class.
      // (QA defect 4: a fillAndKill/fillOrKill no-fill arrives as an error slot
      // — "fill and kill has no matches", rejected_order_type_rules — so this
      // branch swallowed it and the prompt gate was never reached.)
      //
      // FRAGILE BY NECESSITY (spec-review 25): the prose regex is the ONLY
      // signal — the engine is frozen (100%-frontend directive), so no
      // structured code exists. Master's exact strings, verified at
      // place/post.go and place/match.go on the untouched engine:
      //   "fill and kill has no matches"  (errorType rejected_order_type_rules)
      //   "fill or kill has no matches"   (errorType rejected_order_type_rules)
      // If orders-api wording ever changes, this breaks silently — the QA
      // smoke's runtime gate asserts these strings for that reason.
      // LIVE-gated (carried R2): pre-game no-fills are deterministic — the
      // carve-out must not spare them from the rejection exit into a
      // pointless retry.
      const isEngineNoFill = (s) =>
        props.fullGame.live &&
        s?.errorType === 'rejected_order_type_rules' &&
        /no matches/i.test(s?.error || '');

      const rejections = sessions.filter((s) => s?.error && !isEngineNoFill(s));
      lastAttemptHadRejections = rejections.length > 0;
      if (rejections.length === sessions.length) {
        // unchanged: all TRUE rejections (balance/validation) exit with the real reason
        betTakenFailedNotification(placeRejectionNotification(rejections[0]));
        trackLiveBetResult('failed', rejections[0].errorType || 'rejected');
        closeModal();
        return;
      }

      const successes = sessions.filter(
        (s) => !s.error && (s.matched?.length ?? 0) > 0,
      );
      const zeroFillSuccesses = sessions.filter(
        (s) => !s.error && (s.matched?.length ?? 0) === 0,
      );

      // ANY live no-fill — zero-fill SUCCESS (matched: [], spec-review 25
      // G2) or the engine's no-fill ERROR slot (bugbot: the error shape
      // still consumed a second attempt, so users sat through the full
      // live delay TWICE before the prompt) — breaks straight to the
      // re-book path. Retrying is worse than prompting: it re-runs the
      // engine's own-order cleanup side effects and doubles the wait,
      // and the prompt offers the fresh price anyway. Pre-game keeps the
      // silent retry (no prompt exists there).
      if (
        props.fullGame.live &&
        successes.length === 0 &&
        rejections.length === 0 &&
        (zeroFillSuccesses.length > 0 || sessions.some(isEngineNoFill))
      ) {
        break;
      }

      if (successes.length > 0) {
        phOutcome.value = 'bet_taken';
        httpStatus = 'ok';
        placeSessionsForTelemetry = successes;
        const device = currentDevice();

        let sideString;
        let grandRiskWithCommission = 0;
        let grandRiskRaw = 0;
        let grandWinRaw = 0;
        let grandWinWithCommission = 0;

        successes.forEach((session) => {
          session.matched.forEach((bet) => {
            const vigAdjRisk = commissionAdjustedRisk(
              bet.amount,
              bet.odds,
              league,
              orderType,
            );
            const sessionObject = {
              type: bet.type,
              participantID: ['spread', 'moneyline'].includes(bet.type)
                ? bet.side
                : null,
              spread: bet.type === 'spread' ? bet.number : null,
              OU: bet.type === 'total' ? bet.side : null,
              total: bet.type === 'total' ? bet.number : null,
              market: bet.type === 'moneyline1x2' ? bet.market : null,
              side: bet.type === 'moneyline1x2' ? bet.side : null,
            };

            if (
              userStore.user.displayRotationNumbers &&
              betType.value !== 'moneyline1x2'
            ) {
              sideString =
                sessionObject.type !== 'total'
                  ? `${
                      props.fullGame.participants.find(
                        (p) => p.id === sessionObject.participantID,
                      ).rotationNumber
                    } ${getSideString(props.fullGame, sessionObject)}`
                  : props.fullGame.participants
                    ? `${
                        props.fullGame.participants.find(
                          (p) => p.homeAway === 'away',
                        ).rotationNumber
                      } ${getSideString(props.fullGame, sessionObject)}`
                    : `${getSideString(props.fullGame, sessionObject)}`;
            } else {
              sideString = getSideString(props.fullGame, sessionObject);
            }
            grandRiskWithCommission += vigAdjRisk;
            grandRiskRaw += bet.amount;
            grandWinRaw += bet.winWithoutCommission;
            grandWinWithCommission += bet.win ?? bet.winWithoutCommission;
          });
        });

        const gotOddsRaw = calculateAverageOdds(grandRiskRaw, grandWinRaw);
        const gotOddsDisplay = formatOdds(gotOddsRaw, league, orderType, true);
        const gameInfo =
          props.fullGame.participants &&
          props.fullGame.participants.length >= 2
            ? `${props.fullGame.participants[0].longName} vs ${props.fullGame.participants[1].longName}`
            : 'Game Info Not Available';

        const gotRisk = grandRiskWithCommission;
        const gotWin = userStore.user.viewOddsWithCommission
          ? grandWinWithCommission
          : grandWinRaw;

        // Partial = the stake actually sent to the API matched for less than
        // requested. Comparing sent-vs-matched (both raw, commission-excluded)
        // is the single source of truth and never goes stale, unlike tracking
        // typed intent across the risk/win/ladder/max inputs.
        const requestedBetRaw = parseFloat(riskAmt) || 0;
        const partial = requestedBetRaw - grandRiskRaw > 1;
        // Compare projected blended average vs actual blended average. Do NOT
        // compare against worstOdds (the worst single ladder level) — on a
        // multi-level fill the average is naturally better than the worst
        // level, which would flag a "different odds" on a normal full fill.
        const oddsChanged =
          Number.isFinite(wantedOddsRaw) &&
          Number.isFinite(gotOddsRaw) &&
          Math.abs(wantedOddsRaw - gotOddsRaw) >= 0.5;
        // "The tolerance governed" needs an ACTUAL slip, not tolerance-was-
        // active (spec-review 25 G3: a fill at exactly the consented price
        // differs from the 2-decimal projection by float dust — a strict
        // compare opened the summary claiming a slip that didn't happen).
        // Worse for the bettor = higher implied probability, beyond both a
        // prob epsilon and a 0.01-odds-point rounding allowance.
        const slipGoverned =
          slippagePct > 0 &&
          Number.isFinite(wantedOddsRaw) &&
          Number.isFinite(gotOddsRaw) &&
          convertAmericanToImpliedProbability(gotOddsRaw) >
            convertAmericanToImpliedProbability(wantedOddsRaw) + 1e-6 &&
          Math.abs(gotOddsRaw - wantedOddsRaw) > 0.01;
        // 100%-frontend design: no server echo exists — when a slip actually
        // happened, the tolerance that governed is by construction the one
        // this tab floored the ask with.
        const appliedSlippagePct = slipGoverned ? slippagePct : undefined;
        trackLiveBetResult('booked', undefined, {
          applied_slippage_pct: appliedSlippagePct ?? null,
          sent_slippage_pct: sentSlippagePct,
          configured_slippage_pct: slippagePct,
          wire_ask_odds: wireAskOdds,
        });

        if (partial || oddsChanged || slipGoverned) {
          emitter.emit('showFillSummaryModal', {
            gameInfo,
            sideString,
            betType: orderType,
            device,
            appliedSlippagePct,
            // Raw oddsChanged only — a governed sub-threshold slip opens the
            // modal (gate above) and discloses via the tolerance note, but
            // must NOT assert the "Filled at Different Odds" header: at
            // rounding-collision sizes that header displays X vs X
            // (spec-review 22 MED-1).
            reasons: { partial, oddsChanged },
            wanted: {
              odds: wantedOddsDisplay,
              risk: wantedRisk,
              win: wantedWin,
            },
            got: {
              odds: gotOddsDisplay,
              risk: gotRisk,
              win: gotWin,
            },
          });
          emitter.emit('get-me');
        } else {
          betTakenNotification(
            {
              sideString,
              amount: gotRisk,
              odds: gotOddsDisplay,
              betType: orderType,
              gameInfo,
            },
            device,
            userStore,
          );
        }
        closeModal();
        return;
      }

      // No matches and no errors that produced sessions; loop retries if
      // we still have attempts. `loading` stays true the whole time.
    }

    // Engine no-fill after all retries (the response carried a session slot
    // with zero matches). For ANY live bet — strict or slippage — offer a
    // one-tap re-book at the current price instead of dead-ending. Never
    // re-offer when this attempt was itself a re-book (forceStrict): a pinned
    // price that vanished must fail plainly, no prompt loop. Never offer when
    // the response carried explicit rejections (balance/validation) — those
    // are deterministic failures, not price moves (all-rejection responses
    // exit above; this guards mixed multi-order responses).
    if (props.fullGame.live && !forceStrict && !lastAttemptHadRejections) {
      // `loading` stays TRUE through the prompt: it also disables the Take
      // button, and releasing it here would let a second placement race the
      // re-book flow (bugbot). Only the live-delay banner is suppressed,
      // via rebookPromptOpen.
      const handled = await offerRebookAtNewPrice(useSlippage && slippagePct > 0);
      if (handled) return;
    }
    betTakenFailedNotification({
      title: 'Take Order Failed',
      detail: 'This order is no longer available.',
    });
    trackLiveBetResult('failed', 'order_unavailable');
    closeModal();
  } catch (error) {
    // Latch: once a leg came back ok, a throw from the notification / analytics
    // code below it is a UI bug, not a failed placement. Relabelling it 'error'
    // would book a real success as a failure in the telemetry.
    if (httpStatus !== 'ok') httpStatus = 'error';
    betTakenFailed(error);
    trackLiveBetResult('failed', 'error');
    closeModal();
  } finally {
    loading.value = false;
    placeTracker.recordHttpResponse(
      placeAttemptId,
      httpStatus,
      placeSessionsForTelemetry,
    );
    placeIntentKey = generateIdempotencyKey();
  }
}

/**
 * Functionality for setting up / tearing down take bet (Take Bet)
 */

function setUpTakeBet() {
  if (props.session.type === 'spread') {
    alternativeSpreads.value.home = Object.keys(
      props.fullGame.homeSpreads,
    ).sort((a, b) => parseFloat(b) - parseFloat(a));
    alternativeSpreads.value.away = Object.keys(
      props.fullGame.awaySpreads,
    ).sort((a, b) => parseFloat(a) - parseFloat(b));
  } else if (props.session.type === 'total') {
    alternativeTotals.value = Object.keys(props.fullGame.over).sort(
      (a, b) => a - b,
    );
  }
}

/***
 * functionality for setting spreads and totals (Take Bet) (Create Offer)
 ***/
const spread = ref(side.value.spread);
const total = ref(side.value.total);
const alternativeSpreads = ref({
  home: [],
  away: [],
});
const alternativeTotals = ref([]);
const awaySpreadPopUp = ref(false);
const totalPopUp = ref(false);

function handleSelectSpreadOrTotal(payload) {
  const { number, homeAway } = payload;
  if (betType.value === 'spread') {
    spread.value = sideType.value.startsWith(homeAway) ? number : -1 * number;
  } else {
    total.value = number;
  }
  handleRiskUpdate();
}

/**
 * Functionality for calculating bid ask ladder (Take Bet)
 */

// Builds the taken-side ladder for a given game object. Used by the reactive
// bidLadder computed (props.fullGame) AND by the re-book prompt against a
// freshly fetched game — one extraction, no second mapping to drift.
function ladderFromGame(game) {
  let sourcePath;
  const sideKey = sideType.value.side === 'yes' ? 'Yes' : 'No'; // Capitalize for key matching

  // Helper function for deep cloning
  const deepClone = (obj) => {
    if (!obj) return obj;
    return structuredClone(toRaw(obj));
  };
  switch (side.value.type) {
    case 'moneyline':
      sourcePath = game[sideType.value];
      break;
    case 'spread':
      sourcePath = game[sideType.value]?.[spread.value];
      break;
    case 'total':
      sourcePath = game[sideType.value]?.[total.value];
      break;
    case 'moneyline1x2':
      if (props.session.market === 'draw') {
        sourcePath = game['draw1x2']?.[`draw${sideKey}`];
      } else {
        const homeAwayKey = sideType.value.homeAwayDraw;
        sourcePath =
          game[homeAwayKey]?.[
            `${homeAwayKey.replace('Moneylines1x2', '')}${sideKey}`
          ]; // Simplified logic
      }
      break;
    default:
      sourcePath = null; // Handle unknown types gracefully
  }
  const bids = deepClone(sourcePath); // Clone once at the end
  return formatBidAndAsk(
    bids,
    game.league.toLowerCase(),
    props.session.type,
  );
}
const bidLadder = computed(() => ladderFromGame(props.fullGame));

const askLadder = computed(() => {
  const otherSide = getOtherSideType(sideType.value);
  const sideKey = sideType.value?.side === 'yes' ? 'No' : 'Yes'; // Add optional chaining for safety

  // Helper function for deep cloning
  const deepClone = (obj) => {
    if (!obj) return obj;
    return structuredClone(toRaw(obj));
  };

  let sourcePath;

  switch (side.value.type) {
    case 'moneyline':
      sourcePath = props.fullGame[otherSide];
      break;
    case 'spread':
      sourcePath = props.fullGame[otherSide]?.[-spread.value]; // Assuming this is intentional; verify spread.value type
      break;
    case 'total':
      sourcePath = props.fullGame[otherSide]?.[total.value];
      break;
    case 'moneyline1x2':
      if (props.session.market === 'draw') {
        sourcePath = props.fullGame['draw1x2']?.[`draw${sideKey}`];
      } else {
        const homeAwayKey = sideType.value.homeAwayDraw;
        sourcePath =
          props.fullGame[homeAwayKey]?.[
            `${homeAwayKey.replace('Moneylines1x2', '')}${sideKey}`
          ];
      }
      break;
    default:
      sourcePath = null; // Handle unknown types gracefully
  }

  const asks = deepClone(sourcePath); // Clone only if sourcePath is defined
  return formatBidAndAsk(
    asks,
    props.fullGame.league?.toLowerCase() ?? '',
    props.session.type,
  );
});

/**
 * Functionality that is HTML and CSS Related (Take Bet) (Create Offer)
 */
const showArrow = ref(false);
const loading = ref(false);
const isMobileViewport = computed(() => screenWidth.value < 768);
const takeBetDisabled = computed(
  () =>
    risk.value === '' ||
    parseFloat(risk.value) < 1 ||
    !validateZero.value ||
    available.value < 1 ||
    updatingMaxRisk.value ||
    orderbookChangedDuringEntry.value ||
    loading.value,
);
// One Idempotency-Key per bet intent, rotated after each completed placement
// cycle. A duplicated submission of the same intent replays the first
// response server-side instead of placing a second bet.
let placeIntentKey = generateIdempotencyKey();
const onFocusValues = ref({
  risk: false,
  win: false,
  createOfferRisk: false,
  createOfferWin: false,
  odds: false,
  expiry: false,
  keyboard: false,
  max: true,
});
const dropdownWin = ref(false);
const dropdownRisk = ref(false);
onClickOutside(riskDropdownRef, () => {
  dropdownRisk.value = false;
});
onClickOutside(winDropdownRef, () => {
  dropdownWin.value = false;
});
const bidLadderSpread = ref(false);
// formerly disabledOrEnabled
const createOfferDisabled = ref(false);

// Analytics: capture the whole modal interaction as one open + one close.
const phIsLive = computed(() => !!props.fullGame?.live);
const phMarketType = computed(() =>
  props.fullGame?.league?.includes('PROPS') ? 'prop' : props.session?.type,
);
// Exotic-market label (to_advance / 1x2 / euro_handicap / half_line / series /
// standard) — see getMarketSubtype for the decision this feeds.
const phMarketSubtype = computed(() =>
  getMarketSubtype(props.fullGame, props.session?.type),
);
const phOutcome = ref('abandoned');
const phModeSwitchCount = ref(0);
// Joins this modal's product-analytics events to the placeUpdateReceived GCP metric.
const phPlaceAttemptId = ref(null);

// Live-only: a click can still bounce (price moved in the delay window), so we
// track the try separately from the result we read off the booked/failed
// popup. Non-live needs none of this — there a click is the bet.
function trackLiveBetAttempt() {
  if (!phIsLive.value) return;
  PostHogUtils.capture('live_bet_attempted', {
    market_type: phMarketType.value,
  });
}
function trackLiveBetResult(outcome, reason, extraProps = {}) {
  if (!phIsLive.value) return;
  PostHogUtils.capture('live_bet_result', {
    outcome,
    reason: reason || null,
    market_type: phMarketType.value,
    market_subtype: phMarketSubtype.value,
    ...extraProps,
  });
}

// Slide-to-confirm friction. The slider replaced the tap button on mobile and
// needed three feel-fix PRs — decision: did it add friction to the final step
// of every mobile bet (keep it, tune it, or go back to a tap)? 'abandoned' =
// a real drag attempt (crossed the control's travel threshold) with no
// confirmed slide by the time the modal closes. The control emits 'abandon'
// at the attempt crossing, not at gesture end, so attempts that die without
// a pointer event (short release, scroll steal, disable reset, unmount
// mid-drag) still count. One abandoned max per modal open, and a completed
// slide cancels a pending abandoned — the user got there in the end, which
// isn't the friction being measured. 'completed' fires at gesture
// completion, not bet success: this event measures the control; bet outcomes
// are bet_modal_closed / live_bet_result.
const phSlideAbandonPending = ref(false);
function onSlideAbandon() {
  phSlideAbandonPending.value = true;
}
function onSlideConfirm() {
  phSlideAbandonPending.value = false;
  PostHogUtils.capture('bet_confirm_slide', {
    outcome: 'completed',
    surface: 'take',
  });
  if (exceedsBalanceButCanBetMax.value) {
    takeBetAtMax();
  } else {
    takeBet();
  }
}

const validateZero = computed(() => {
  const winV = win.value;
  const riskV = risk.value;

  if (riskV[0] === '0' || winV[0] === '0') {
    return false;
  }
  return true;
});

//Reference value for input risk
const referencedRisk = ref(null);
const { width: screenWidth } = useWindowSize();

onMounted(() => {
  // Slippage seeds from the saved Account default via the watcher above — do
  // NOT reset it here (that left the readout at 0% while the thumb sat at the
  // saved value).
  if (screenWidth.value > 1022 && referencedRisk.value) {
    referencedRisk.value.focus();
  }
  window.addEventListener('click', dropdownListener);
  Sentry.addBreadcrumb({
    category: 'takebet-modal',
    message: `TAKEBET MODAL - ${userStore.user.username} opened modal, side ${formattedSideString.value}`,
    level: 'info',
  });
  // "Wants to bet but has no funds" signal — the activation moment behind
  // new-user abandonment. null when not logged in or balance is unknown, so we
  // never report a misleading false; omit the key in that case.
  const emptyBalance = resolveEmptyBalance(userStore.user);
  // Where this bet window came from. Seven surfaces can open this modal, so
  // rather than thread a prop through every one, read the marker the Best
  // Offers feed already writes to the store when a row is clicked
  // (setBestOfferInfo, cleared when the modal closes). Attribution then holds
  // wherever the modal is mounted, and doesn't depend on the feed's own click
  // event, which has never recorded a single click since it shipped.
  // Identity, not id equality: the feed path hands this modal the very object
  // it stored, while every other path builds its own from a fresh fetch. The
  // marker outlives some closes (route changes skip the parent's reset, and it
  // is also read by the socket update path, so this modal must not clear it),
  // and an id comparison would then credit a later grid open of the same order
  // to the feed. A socket refresh replacing the stored object only costs an
  // attribution, which is the safe direction to fail.
  const fromBestOffers =
    !!props.session && props.session === exchangeStore.bestOfferSession;
  // Same store-marker pattern for the props page (AllProps writes the marker
  // in its makeBet just before mounting this modal, cleared on its close
  // handler). Identity comparison again, and the two markers are written by
  // different surfaces, so they can never both match one session object.
  // Carries the navigation state at click time (category pill + grouping) —
  // the props-page-redesign decision needs "what led to a bet", not just
  // that one happened.
  const propsCtx = exchangeStore.propsBetContext;
  const fromProps = !!props.session && props.session === propsCtx?.session;
  PostHogUtils.capture('bet_modal_opened', {
    is_live: phIsLive.value,
    market_type: phMarketType.value,
    market_subtype: phMarketSubtype.value,
    opened_mode: offerMode.value ? 'offer' : 'take',
    ...(emptyBalance !== null && { empty_balance: emptyBalance }),
    ...(fromBestOffers && { source: 'best_offers' }),
    ...(fromProps && {
      source: 'props',
      props_grouping: propsCtx.grouping,
      // Omitted (not null) when no pill was active and the prop's own
      // category couldn't be derived from the keyword catalog.
      ...(propsCtx.category && { prop_category: propsCtx.category }),
    }),
  });
});

onBeforeUnmount(() => {
  // Kill any pending/visible re-book prompt with the slip (spec-review 20
  // M5): a prompt surviving its slip offers a bet from dead closure state.
  slipAlive = false;
  emitter.emit('hidePriceMovedModal');
  window.removeEventListener('click', dropdownListener);
  Sentry.addBreadcrumb({
    category: 'takebet-modal',
    message: `TAKEBET MODAL - ${userStore.user.username} closed modal, side ${formattedSideString.value}`,
    level: 'info',
  });
  PostHogUtils.capture('bet_modal_closed', {
    outcome: phOutcome.value,
    final_mode: offerMode.value ? 'offer' : 'take',
    mode_switch_count: phModeSwitchCount.value,
    is_live: phIsLive.value,
    market_type: phMarketType.value,
    market_subtype: phMarketSubtype.value,
    // Present only when a placement was attempted; joins to the placeUpdateReceived metric.
    ...(phPlaceAttemptId.value && {
      place_attempt_id: phPlaceAttemptId.value,
    }),
  });
  // Deferred abandoned-slide fire: held until close so a later completed
  // slide in the same open could cancel it (see onSlideConfirm).
  if (phSlideAbandonPending.value) {
    PostHogUtils.capture('bet_confirm_slide', {
      outcome: 'abandoned',
      surface: 'take',
    });
  }
});

function openDropdown() {
  bidLadderSpread.value = !bidLadderSpread.value;
  if (props.fullGame.league === 'UFCMMA') {
    return;
  }
  if (props.session.type === 'spread') {
    awaySpreadPopUp.value = true;
  } else {
    totalPopUp.value = true;
  }
}

// Live games get their own Pro Mode preference: a trader can make markets
// pregame but still open straight into take mode once a game goes live, without
// flipping the account setting back and forth. A game with no live flag falls
// back to the pregame preference. See PRO-189.
const offerMode = ref(
  props.fullGame?.live ? !!userStore.user.isProLive : !!userStore.user.isPro,
);

// Slip slippage dial. No toggle: 0% is off, any value above 0 is on. The dial
// DEFAULTS to the user's saved Account setting (Live Bet Odds Slippage): a
// saved 1% opens the live slip at 1%, "No Slippage" opens at 0. Dragging is a
// per-bet override for THIS bet only — it does not rewrite the saved default.
// Placement gates on this dial value directly (the useSlippage guard at
// submit), so the order can never book past what the bar shows.
const betSlippagePct = ref(0);
// Continuous mirror of the dial position for a SMOOTH thumb. betSlippagePct is
// the canonical one-decimal value (display %, worst-fill floor, wire, default
// button); slipThumbPct is the raw continuous position that drives ONLY the
// thumb, track fill, and colour — so the bar glides instead of stepping in 0.1
// jumps. applySlipRaw keeps the two in lockstep.
const slipThumbPct = ref(0);
function applySlipRaw(raw) {
  const clamped = Math.max(0, Math.min(liveSlippageMaxPct, Number(raw) || 0));
  slipThumbPct.value = clamped;
  // Snap the canonical value to one decimal — the wire floor must stay a clean
  // one-decimal value (master validation rejects raw floats).
  betSlippagePct.value = Math.round(clamped * 10) / 10;
}
const showSlippageCard = computed(
  () => props.fullGame.live && !offerMode.value && userStore.user.isLoggedIn,
);
// Seed the dial from the saved setting, and keep it synced with login hydration
// and cross-tab/settings changes. No-slippage or unset → 0 (off). This only
// tracks the STORE, so a mid-slip drag (which doesn't write the store) is never
// clobbered; a fresh slip re-seeds from the saved default on mount.
watch(
  () => [userStore.user.liveSlippageMode, userStore.user.liveSlippagePct],
  ([mode, pct]) => {
    applySlipRaw(
      mode === 'slippage' ? Math.min(liveSlippageMaxPct, Number(pct) || 0) : 0,
    );
  },
  { immediate: true },
);
// Keyboard (arrow keys) on the native input.
function onDialInput(event) {
  applySlipRaw(Number(event.target.value));
}
// Pointer-driven track: a press or drag anywhere on the bar sets the value at
// that x-position (not just grabbing the thumb from its resting spot). We take
// full pointer control so behavior is identical across browsers.
const SLIDER_THUMB_PX = 16;
let sliderDragging = false;
function sliderValueFromPointer(event) {
  const rect = event.currentTarget.getBoundingClientRect();
  // Thumb center travels from half-a-thumb in on each end, so inset the usable
  // track by one thumb width to keep the value under the cursor exact.
  const usable = Math.max(1, rect.width - SLIDER_THUMB_PX);
  const ratio = Math.min(
    1,
    Math.max(0, (event.clientX - rect.left - SLIDER_THUMB_PX / 2) / usable),
  );
  return ratio * liveSlippageMaxPct; // continuous; snapped in applySlipRaw
}
function onSliderPointerDown(event) {
  // Primary button / touch / pen only — ignore right/middle clicks.
  if (event.button != null && event.button > 0) return;
  sliderDragging = true;
  event.currentTarget.setPointerCapture?.(event.pointerId);
  // preventDefault suppresses the native jump so ours is the only one; focus
  // manually so arrow-key nudging still works after a click.
  event.preventDefault();
  event.currentTarget.focus?.();
  applySlipRaw(sliderValueFromPointer(event));
}
// Coalesce drag updates to one per animation frame so the bar glides at the
// display's refresh rate. Raw pointermove can fire 120+/s; without this the
// live worst-fill readout (a ladder scan) reruns on every event and the drag
// stutters. rAF caps all of that — thumb, colour, and readout — to ~60fps.
let pendingSlipPct = null;
let slipMoveRaf = null;
function flushSlipPct() {
  slipMoveRaf = null;
  if (pendingSlipPct != null) {
    applySlipRaw(pendingSlipPct);
    pendingSlipPct = null;
  }
}
function onSliderPointerMove(event) {
  if (!sliderDragging) return;
  pendingSlipPct = sliderValueFromPointer(event);
  if (slipMoveRaf == null) slipMoveRaf = requestAnimationFrame(flushSlipPct);
}
function onSliderPointerUp(event) {
  if (!sliderDragging) return;
  sliderDragging = false;
  if (slipMoveRaf != null) {
    cancelAnimationFrame(slipMoveRaf);
    slipMoveRaf = null;
  }
  // Apply the final position exactly, no partial-frame lag.
  flushSlipPct();
  event.currentTarget.releasePointerCapture?.(event.pointerId);
}
// Grey → vivid blue-purple. The whole card — background wash, left rail,
// status icon, track fill and thumb — lerps from a light grey (0%, off) to a
// saturated purple as the tolerance climbs. sqrt ease front-loads the shift so
// even a small nudge shows clear colour; the tint alpha climbs too so the card
// itself, not just the rail, visibly deepens.
const SLIP_GREY = [170, 172, 184];
const SLIP_ACCENT = [116, 74, 222];
const slipColorT = computed(() =>
  Math.sqrt(
    Math.max(
      0,
      Math.min(1, (Number(slipThumbPct.value) || 0) / liveSlippageMaxPct),
    ),
  ),
);
const slipAccentRgb = computed(() =>
  SLIP_GREY.map((g, i) => Math.round(g + (SLIP_ACCENT[i] - g) * slipColorT.value)),
);
const slipAccentColor = computed(() => `rgb(${slipAccentRgb.value.join(', ')})`);
const slipTintColor = computed(() => {
  const [r, g, b] = slipAccentRgb.value;
  // 0.14 (barely-there grey) → 0.42 (clear purple wash) across the range.
  return `rgba(${r}, ${g}, ${b}, ${(0.14 + 0.28 * slipColorT.value).toFixed(3)})`;
});
// "Learn more" — mirror the Arb Offer card: surface a styled explainer modal
// in place (no navigation away from the slip) via the global emitter.
function openSlippageInfo() {
  emitter.emit('openSlippageInfoModal');
}
// Track-fill stop for the card's slider — same thumb-inset math as the
// account page (center = f·100% − (f−0.5)·16px).
const slipSliderFillStop = computed(() => {
  const f = Math.max(
    0,
    Math.min(1, (Number(slipThumbPct.value) || 0) / liveSlippageMaxPct),
  );
  return `calc(${(f * 100).toFixed(2)}% - ${((f - 0.5) * 16).toFixed(1)}px)`;
});
// "Set as default" — persist the current dial value as the Account setting so
// every future live bet opens at it (3.2% → always 3.2%). 0% saves as No
// Slippage. Device-local: the setting is 100% frontend / localStorage-backed.
function setSlippageDefault() {
  const pct = Number(betSlippagePct.value) || 0;
  const modeKey = pct > 0 ? 'slippage' : 'strict';
  userStore.saveLiveSlippageSetting(modeKey, pct);
  PostHogUtils.captureSettingChange(
    'live_slippage_mode',
    'takebet_default_btn',
    modeKey,
  );
  PostHogUtils.syncSettings({
    live_slippage_mode: modeKey,
    live_slippage_pct: pct,
  });
  emitter.emit('showAlert', {
    type: 'profile-updated',
    shown: true,
    status: 'ok',
    title: 'Default slippage updated',
    detail:
      pct > 0
        ? `Live bets now default to ${pct}% slippage.`
        : 'Live bets now default to No Slippage.',
  });
}
// Live worst-fill readout (right side of the action row). Anchor depends only
// on the selection/stake — NOT betSlippagePct — so it stays cached through a
// drag; only the light floor arithmetic reruns per tick (rAF-capped above).
// Mirrors the bet path exactly (committedWorstOdds → ladder worst → projection)
// so the worst price shown can never beat the price the order floors from.
const slipPriceAnchor = computed(() => {
  const committed = committedWorstOdds.value;
  if (Number.isFinite(committed)) return committed;
  const worstForStake = currentLadderWorstFor(parseFloat(risk.value) || 0);
  if (Number.isFinite(worstForStake)) return worstForStake;
  const projected = parseFloat(computedOdds.value);
  return Number.isFinite(projected) ? projected : null;
});
// The displayed price the dial floors down FROM (stable while scrolling).
const slipPriceDisplay = computed(() => {
  const anchor = slipPriceAnchor.value;
  if (anchor === null) return null;
  return formatOdds(
    anchor,
    props.fullGame.league.toLowerCase(),
    props.session.type,
    true,
  );
});
// The worst price the dial books down to at the current tolerance — this moves
// as you scroll. Null when off (0%) or with no anchor yet.
const slipWorstDisplay = computed(() => {
  const anchor = slipPriceAnchor.value;
  const pct = Number(betSlippagePct.value) || 0;
  if (anchor === null || pct <= 0) return null;
  const league = props.fullGame.league.toLowerCase();
  const rawPts = rawSlippageForDisplayedTolerance(
    anchor,
    pct,
    league,
    props.session.type,
  );
  const { floor } = effectiveFloorFor(anchor, rawPts);
  return formatOdds(floor, league, props.session.type, true);
});
setUpTakeBet();

/***
 * Misc Functionality (Create Offer) (Take Bet)
 */
function secondsByLeague() {
  const { league } = props.fullGame;
  if (league.includes('WORLD-CUP')) {
    return 2;
  }
  if (league.includes('MLB')) {
    return 2;
  } else if (league.includes('NBA')) {
    return 3;
  } else if (league.includes('NFL')) {
    return 3;
  } else if (league.includes('NCAAF')) {
    return 3;
  } else if (league.includes('NCAAB')) {
    return 5;
  } else if (league.includes('UFCMMA')) {
    return 3;
  } else if (league.includes('ATP') || league.includes('WTA')) {
    return 7;
  }
  return 5;
}

/***
 * Set up / Tear Down Create offer Mode (Create Offer)
 */
function setUpCreateOffer() {
  if (!offerMode.value) phModeSwitchCount.value += 1;
  offerMode.value = true;
}

function returnToTakeBet() {
  if (offerMode.value) phModeSwitchCount.value += 1;
  offerMode.value = false;
  // Coming back from offer mode, start take-bet with a clean slate — the user
  // is picking a fresh take, not inheriting whatever risk/win the offer (or a
  // pre-offer ladder click) left behind. Route through commitSelectedBids so
  // the consent anchor (committedWorstOdds) resets too — clearing selectedBids
  // directly left it pinned to the previous selection's price, so the slippage
  // floor readout and a later submit could reuse consent the slip no longer
  // shows.
  commitSelectedBids([]);
  combinedOdds.value = null;
  risk.value = '';
  win.value = '';
}

function toggleLoading(value) {
  loading.value = value;
}

function closeModal() {
  emit('closeModal');
}

function closeDropdown() {
  bidLadderSpread.value = false;
}

//Function to close dropdowns when clciked outside
function dropdownListener(e = {}) {
  if (
    dropdownRisk.value &&
    (!e.target ||
      (e.target.className !== 'input-group-div' &&
        e.target.dataset.icon !== 'angle-up'))
  ) {
    dropdownRisk.value = false;
  }
  if (
    dropdownWin.value &&
    (!e.target ||
      (e.target.className !== 'input-group-div' &&
        e.target.dataset.icon !== 'angle-up'))
  ) {
    dropdownWin.value = false;
  }
}
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/main.scss';

$blue1: #dfe1ef;
$blue2: #343b81;
$border-radius-button: 35px;

h1 {
  font-size: 1.5rem;
  color: main.$blue-1100;
  font-weight: bold;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 1rem;
  }
}

h4 {
  font-size: 0.85rem;
  font-weight: bold;
}

h5 {
  font-size: 0.8rem;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 0.5rem;
  }

  &.header-info {
    color: main.$black-203;
  }
}

h6 {
  color: main.$blue-1100;
  font-weight: main.$font-weight-light;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 0.7rem;
  }
}

.h2 {
  font-size: main.$font-size-sm;
  color: main.$blue-1100;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 0.8rem;
  }
}

input[type='text'] {
  flex-grow: 1;
  min-width: 0;
  font-size: 16px;
  font-weight: bold;
}

//this difference not best was put like this}

.create-offer-btn {
  width: 100%;
  font-size: 0.925rem;
  padding: 0.875rem 1rem;
  background-color: main.$grey-1100_01;
  color: main.$color-primary_01;
  border-radius: $border-radius-button;

  &:disabled {
    background-color: main.$grey-1800;
    color: main.$white;
  }

  &:focus {
    background: main.$blue-103_01;
  }
}

.take-bet-btn {
  width: 100%;
  font-size: 0.925rem;
  padding: 0.875rem 1rem;
  background-color: main.$color-primary_07;
  color: main.$grey-1100_02;
  border-radius: $border-radius-button;

  &:disabled {
    background-color: main.$grey-1800;
    color: main.$white;
  }

  &:focus {
    background: main.$blue-1101;
  }

  &.btn-max-balance {
    background-color: main.$orange-100;
    color: main.$white;

    &:hover {
      filter: brightness(0.95);
    }
  }
}

.risk-win-items {
  justify-content: space-between;
}

.tick {
  background-color: main.$grey-500;
  user-select: none;
}

.telegramTick {
  background-color: main.$grey-402_02;
  user-select: none;
  box-shadow: 0 2px 4px 0 main.$blue-1200;
}

.info {
  box-shadow: 0 2px 4px 0 main.$blue-1200;
  background-color: main.$white-200;
}

.input-group {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  outline: 1px solid main.$grey-300;
  box-shadow: 0 2px 10px 0 main.$blue-1200;
  border-radius: 12px;
  padding: 8px 0 8px 5px;
  color: main.$blue-800_01;

  input {
    width: calc(100% - 100px);
    background-color: main.$transparent_01;
    padding: 0 0.5rem;
    font-size: 18px;

    &:focus {
      border-bottom-color: main.$transparent_01;
    }
  }

  .input-group-button {
    width: 56px;

    .available {
      display: inline-block;
      float: right;
      color: main.$white;
      // Premium quick-add button: indigo gradient with a soft glow + top gloss.
      background: linear-gradient(180deg, #7b83d8 0%, main.$blue-500 100%);
      padding: 7px 12px;
      font-size: 0.8rem;
      font-weight: 800;
      letter-spacing: 0.01em;
      border-radius: 9px;
      cursor: pointer;
      box-shadow:
        0 2px 8px -1px rgba(78, 103, 223, 0.67),
        inset 0 1px 0 rgba(255, 255, 255, 0.23);
      transition:
        box-shadow 0.16s ease,
        transform 0.16s ease;

      &:hover {
        box-shadow:
          0 4px 14px -2px rgba(78, 103, 223, 0.8),
          inset 0 1px 0 rgba(255, 255, 255, 0.33);
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  .input-group-div {
    width: 100px;
    display: inline;
    padding: 0 0.3rem;
    border-left: 2px solid main.$grey-900;
    align-items: center;
    font-weight: 900;
    cursor: pointer;
    font-size: 16px;
    text-align: center;
  }

  &.input-group-win-risk {
    input {
      width: calc(100% - 100px - 56px);
    }
  }
}

.dropdown-menu {
  background-color: main.$white;
  overflow: hidden;
  z-index: 1;
  width: 80px;
  position: absolute;
  border: 1px solid main.$black-200;
  border-radius: 5px;
  top: -200px;
  right: -5px;

  &.expiry {
    width: 100px;
    top: -120px;

    @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
      width: 40%;
    }
  }

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    right: 0;
    width: 40%;
  }

  div {
    width: 100%;
    font-size: 16px;
    padding: 0.5rem 1rem;
    clear: both;
    font-weight: 400;
    text-align: inherit;
    white-space: nowrap;
    background-color: main.$transparent_03;
    border-bottom: 1px solid main.$blue-102;
    cursor: pointer;

    &:last-child {
      border-bottom: main.$transparent;
    }

    &:hover {
      background-color: main.$white-101;
      font-weight: bold;
    }
  }

  .quick-max {
    color: main.$blue-500;
    font-weight: 700;
  }
}

.risk-win-items {
  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    display: contents;

    > div {
      padding: 0 !important;
      margin: 10px 0;
    }
  }
}

.add-balance {
  text-decoration: underline;
  color: main.$red-500_03;
  font-weight: 700;
  cursor: pointer;
}

.add-available-risk {
  text-decoration: underline;
  color: main.$color-primary_01;
  font-weight: 700;
  cursor: pointer;
}

.max-risk {
  color: main.$color-primary_01;
}

.rebate-line {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(46, 125, 50, 0.08);
  border: 1px solid rgba(46, 125, 50, 0.2);
  border-radius: 8px;

  &__label {
    font-size: 0.85rem;
    color: main.$black-203;
    font-weight: 500;
  }

  &__value {
    font-size: 0.95rem;
    color: #2e7d32;
    font-weight: 700;
  }
}

/* Compact info strip docked above the Take-bet button while a live bet is
   held in the server's confirm window. Subtle by design — the disabled
   action button is the primary loading signal, this just explains why. */
.live-delay-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 6px 10px;
  border-radius: 6px;
  background: rgba(99, 102, 241, 0.06);
  border: 1px solid rgba(99, 102, 241, 0.2);
  color: main.$black;
  font-size: 13px;
  line-height: 1.3;

  &__dot {
    flex: 0 0 auto;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #6366f1;
    animation: live-delay-pulse 1.2s ease-in-out infinite;
  }

  &__text {
    flex: 1 1 auto;
  }
}

@keyframes live-delay-pulse {
  0%, 100% { opacity: 0.35; }
  50%      { opacity: 1; }
}

[theme='night'] .live-delay-banner {
  background: rgba(129, 140, 248, 0.08);
  border-color: rgba(129, 140, 248, 0.25);
  color: #e5e7eb;

  &__dot { background: #818cf8; }
}

.propStyle {
  display: -webkit-inline-box;
}

/* Inline pill that lives at the end of the matchup line in the modal header.
   Compact + secondary-weight so it reads as a tag, not a banner. */
.must-start-pill {
  display: inline-block;
  vertical-align: middle;
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  background: rgba(245, 158, 11, 0.15);
  color: #b45309;
  line-height: 1.4;
}

[theme='night'] .must-start-pill {
  background: rgba(251, 191, 36, 0.16);
  color: #fcd34d;
}

/* Period sticker (1H / F5 / 2H / Q1 …). Same compact pill shape as MUST START
   but a distinct blue so it reads as "this is a partial-game market". */
.period-pill {
  display: inline-block;
  vertical-align: middle;
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  background: rgba(37, 99, 235, 0.15);
  color: #1d4ed8;
  line-height: 1.4;
}

[theme='night'] .period-pill {
  background: rgba(96, 165, 250, 0.18);
  color: #93c5fd;
}

.must-start-flag {
  display: inline-block;
  align-self: center;
  margin: 0 auto;
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(217, 119, 6, 0.12);
  color: #b45309;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

[theme='night'] .must-start-flag {
  background: rgba(251, 191, 36, 0.16);
  color: #fcd34d;
}

/* Per-bet slippage card. No toggle — 0% is off, any value is on, and it always
   opens at 0. Colour is continuous, not binary: the card wash, its left rail,
   the status icon, and the track fill all lerp light-grey → blue-purple via
   --slip-accent/--slip-tint (set inline from the dial value). Scoped styles —
   no bleed elsewhere. */
.arb-card {
  position: relative;
  overflow: hidden;
  border-radius: 10px;
  background-color: var(--slip-tint, rgba(170, 172, 184, 0.14));
  border: 1px solid rgba(0, 0, 0, 0.04);
  transition: background-color 0.12s linear;

  // One continuous left rail drawn ON TOP of the row backgrounds — an inset
  // box-shadow would be covered by the slider/floor rows' white fill and read
  // as two-toned down the left edge.
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    z-index: 2;
    background: var(--slip-accent, rgb(170, 172, 184));
    transition: background 0.12s linear;
    pointer-events: none;
  }

  [theme='night'] & {
    border-color: rgba(255, 255, 255, 0.06);
  }
}
.arb-card-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.8rem 0.5rem 0.9rem;
  color: main.$blue-1100;

  [theme='night'] & {
    color: rgba(255, 255, 255, 0.9);
  }
}
.arb-card-status-icon {
  flex-shrink: 0;
  font-size: 0.9rem;
  color: var(--slip-accent, rgb(170, 172, 184));
  transition: color 0.12s linear;
}
.arb-card-text {
  font-size: 0.9rem;
  line-height: 1.4;
  font-weight: main.$font-weight-semibold;

  @media screen and (max-width: map.get(main.$grid-breakpoints, 'sm')) {
    font-size: 0.8rem;
  }
}
/* Learn more — inline at the end of the message copy (right after the
   sentence), not a standalone control. */
.arb-card-learn {
  margin-left: 0.3rem;
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.82rem;
  color: main.$color-primary_01;
  opacity: 0.8;
  white-space: nowrap;

  &:hover {
    opacity: 1;
  }
}
/* The dial row, a distinct sub-panel below the message. */
.arb-card-slider-row {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.4rem 0.8rem 0.45rem 0.9rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.55);
  cursor: default;

  [theme='night'] & {
    background: rgba(255, 255, 255, 0.04);
    border-top-color: rgba(255, 255, 255, 0.06);
  }
}
.slippage-value {
  min-width: 3.1rem;
  text-align: right;
  font-weight: 700;
  font-size: 0.9rem;
  color: main.$color-primary_01;
}
/* "Set as default" action row below the dial. */
.arb-card-default-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.7rem;
  padding: 0.4rem 0.8rem 0.5rem 0.9rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);

  [theme='night'] & {
    border-top-color: rgba(255, 255, 255, 0.06);
  }
}
.arb-card-default-btn {
  flex: 0 0 auto;
  border: 1px solid #744ade;
  border-radius: 7px;
  padding: 0.35rem 0.65rem;
  background: transparent;
  color: #744ade;
  font-size: 0.8rem;
  font-weight: main.$font-weight-semibold;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 0.12s ease,
    opacity 0.12s ease;

  &:hover {
    background: rgba(116, 74, 222, 0.08);
  }
  &:active {
    transform: translateY(1px);
  }
}
/* Live worst-fill readout on the right of the action row. Fixed-width value
   slot so only the numbers move while scrolling — the row never reflows. */
.arb-card-floor-value {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  flex-shrink: 0;
  font-size: 0.9rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: main.$blue-1100;

  [theme='night'] & {
    color: rgba(255, 255, 255, 0.92);
  }
}
.arb-card-floor-from {
  opacity: 0.55;
}
.arb-card-floor-arrow {
  opacity: 0.55;
}
.arb-card-floor-to {
  min-width: 3.1rem;
  text-align: right;
  color: var(--slip-accent, #{main.$color-primary_01});
}

/* Range slider — Account/Index.vue .slippage-slider verbatim (custom track
   so the fill hugs the 16px thumb; global input CSS zeroed). */
.slippage-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 16px;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  outline: none;
  cursor: pointer;
  // We drive the value from pointer x-position; block touch-scroll so a drag
  // on the bar moves the dial instead of panning the page.
  touch-action: none;

  &:hover,
  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
    box-shadow: none;
    border: none;
  }
  &::-moz-focus-outer {
    border: 0;
  }

  &::-webkit-slider-runnable-track {
    height: 8px;
    border-radius: 4px;
    background: linear-gradient(
      to right,
      var(--slip-accent, #{main.$color-primary}) 0%,
      var(--slip-accent, #{main.$color-primary}) var(--fill, 8px),
      main.$grey-1901 var(--fill, 8px),
      main.$grey-1901 100%
    );
  }
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    margin-top: -4px;
    border-radius: 50%;
    background: var(--slip-accent, #{main.$color-primary});
    cursor: pointer;
  }
  &::-moz-range-track {
    height: 8px;
    border-radius: 4px;
    background: linear-gradient(
      to right,
      var(--slip-accent, #{main.$color-primary}) 0%,
      var(--slip-accent, #{main.$color-primary}) var(--fill, 8px),
      main.$grey-1901 var(--fill, 8px),
      main.$grey-1901 100%
    );
  }
  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border: none;
    border-radius: 50%;
    background: var(--slip-accent, #{main.$color-primary});
    cursor: pointer;
  }
}
</style>
